import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Slider,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import RefreshIcon from '@mui/icons-material/Refresh';
import PublicIcon from '@mui/icons-material/Public';
// Topic icons (Google Material) — keyword-matched to authority topics.
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SavingsIcon from '@mui/icons-material/Savings';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HandshakeIcon from '@mui/icons-material/Handshake';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ScienceIcon from '@mui/icons-material/Science';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import EcoIcon from '@mui/icons-material/Spa';
import BoltIcon from '@mui/icons-material/Bolt';
import MemoryIcon from '@mui/icons-material/Memory';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import CampaignIcon from '@mui/icons-material/Campaign';
import SchoolIcon from '@mui/icons-material/School';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import InsightsIcon from '@mui/icons-material/Insights';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import GroupsIcon from '@mui/icons-material/Groups';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import CategoryIcon from '@mui/icons-material/Category';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
// Geographic-scope icons for the zones picker.
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MapIcon from '@mui/icons-material/Map';
import FlagIcon from '@mui/icons-material/Flag';
import LanguageIcon from '@mui/icons-material/Language';
import type {
  AudienceArchetype,
  AudienceMix,
  Brand,
  BrandObjective,
  BrandStrategy,
  Envergure,
  IndustryCode,
  ObjectiveMix,
} from '@traaker/shared';
import {
  INDUSTRY_CODES,
  INDUSTRY_LABELS_FR,
  INDUSTRY_LABELS_EN,
  industryLabels,
  isOnboardingComplete,
} from '@traaker/shared';
import {
  OBJECTIVE_PRESETS,
  GEO_GENERATE_MAX,
  resolveEntitlement,
  estimateAuditCost,
  CREDIT_COST,
  WEBSITE_AUDIT_AGENTS,
  describeCadence,
  BRAND_STRATEGY_LIMITS,
  type Cadence,
} from '@traaker/shared';
import { Trans, useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { pushDataLayerEvent } from '../utils/analytics';
import { formatNumber } from '../i18n/format';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { useCredits } from '../contexts/CreditContext';
import { pruneEmpty } from '../components/brand/strategy-sections';
import { DistributionEditor } from '../components/brand/DistributionEditor';
import LanguageSelect from '../components/common/LanguageSelect';
import { normalizeUiLanguage, type UiLanguage } from '../i18n/languages';
import {
  brandQueriesApi,
  type GeneratedQueryProposal,
  type QueryExampleRow,
} from '../services/brand-queries';
import { competitorsApi } from '../services/competitors';
import { useDomainFavicon } from '../hooks/useDomainFavicon';
import { BrandFavicon } from '../components/brand/BrandFavicon';
import { ALL_ENGINES, EngineLogo } from '../components/brand/EngineLogo';

// ─────────────────────────────────────────────────────────────────────────────
// Brand onboarding wizard.
//
// A full-page, gated flow that walks a new brand from creation to first audit:
//   Part 1 — Brand Knowledge  (discover → edit sections → validate)
//   Part 2 — GEO Strategy     (generate → edit → validate)
//   Part 3 — Prompts          (auto-generate per axis → review → apply)
//   Finish — fire the 3 brand-perception audits, route to dashboard.
//
// All endpoints already exist; this component is pure orchestration. Each part
// ends by VALIDATING its strategy layer so the next part's backend pre-flight
// gate passes (GEO generate requires brand validated; audits require both).
// ─────────────────────────────────────────────────────────────────────────────

// Themed onboarding backdrop — matches the account-onboarding Shell (Onboarding.tsx).
const ONBOARDING_BG =
  'radial-gradient(1000px 480px at 50% -8%, rgba(124,58,237,0.12), transparent 60%), ' +
  'linear-gradient(180deg, #FAFAFA 0%, #F4F2FB 100%)';

// Wizard step labels — i18n keys resolved with `t()` at render.
const WIZARD_STEP_KEYS = [
  'onboarding.wizard.stepKnowledge',
  'onboarding.wizard.stepGeo',
  'onboarding.wizard.stepPrompts',
];

const AXES = [
  { axis: 'narrative' as const, labelKey: 'onboarding.axis.narrative', color: '#8B5CF6' },
  { axis: 'authority' as const, labelKey: 'onboarding.axis.authority', color: '#00DDB3' },
  { axis: 'referral' as const, labelKey: 'onboarding.axis.referral', color: '#FF6B35' },
];

// Marketing objective → which axis it leans on. Pre-fills the axis-mix sliders.
// `labelKey`/`hintKey` are i18n keys resolved with `t()` at render.
const OBJECTIVE_OPTIONS: { value: BrandObjective; labelKey: string; hintKey: string }[] = [
  { value: 'leads', labelKey: 'onboarding.geo.objective.leads.label', hintKey: 'onboarding.geo.objective.leads.hint' },
  { value: 'ecosystem', labelKey: 'onboarding.geo.objective.ecosystem.label', hintKey: 'onboarding.geo.objective.ecosystem.hint' },
  { value: 'image', labelKey: 'onboarding.geo.objective.image.label', hintKey: 'onboarding.geo.objective.image.hint' },
];

// Objective-mix segments — colours match the brand-axis palette above.
// `labelKey`/`descriptionKey` are i18n keys resolved with `t()` at render into
// concrete `DistributionSegment`s (which need `label`/`description` strings).
const OBJECTIVE_SEGMENTS: {
  key: keyof ObjectiveMix;
  labelKey: string;
  descriptionKey: string;
  color: string;
}[] = [
  { key: 'referral', labelKey: 'onboarding.axis.referral', descriptionKey: 'onboarding.geo.segment.referral', color: '#FF6B35' },
  { key: 'authority', labelKey: 'onboarding.axis.authority', descriptionKey: 'onboarding.geo.segment.authority', color: '#00DDB3' },
  { key: 'narrative', labelKey: 'onboarding.axis.narrative', descriptionKey: 'onboarding.geo.segment.narrative', color: '#8B5CF6' },
];

const AXIS_TO_AUDIT_TYPE = {
  narrative: 'brand_narrative',
  authority: 'brand_authority',
  referral: 'brand_referral',
} as const;

// Engines each axis actually probes (mirrors orchestrator.ENGINES_BY_AXIS —
// authority excludes ChatGPT only, which returns no citations; mistral runs on
// all three). Used to compute the real request count + credit cost from the
// selected engine allowlist.
const AXIS_ENGINE_ROSTER: Record<'narrative' | 'authority' | 'referral', string[]> = {
  narrative: ['perplexity', 'openai', 'gemini', 'claude', 'mistral'],
  authority: ['perplexity', 'gemini', 'claude', 'mistral'],
  referral: ['perplexity', 'openai', 'gemini', 'claude', 'mistral'],
};

// BP schedule options for the onboarding picker (one cadence for all 3 axes).
// `labelKey` resolved with `t()` at render.
const BP_SCHEDULE_OPTIONS: { labelKey: string; value: Cadence }[] = [
  { labelKey: 'onboarding.prompts.schedule.daily', value: { interval: 1, unit: 'days' } },
  { labelKey: 'onboarding.prompts.schedule.weekly', value: { interval: 1, unit: 'weeks' } },
  { labelKey: 'onboarding.prompts.schedule.biweekly', value: { interval: 2, unit: 'weeks' } },
  { labelKey: 'onboarding.prompts.schedule.monthly', value: { interval: 1, unit: 'months' } },
];

// Credit reserve at onboarding finish: website = maxPages × 3 agents;
// earned_media = maxFetch (1 cr/fetch). These default to the agency tier ceiling
// (100 pages / 100 fetch) but we auto-shrink them to fit the balance so the
// first-run audits never fail the credit gate. Mirrors the Trigger Lambda.
const AGENCY_WEBSITE_MAX_PAGES = 100;
const AGENCY_EM_MAX_FETCH = 100;

// First-ever audit caps for an agency brand: a fixed, modest budget for the
// website + earned-media first runs (30 pages → 90 cr, 90 fetches → 90 cr) so
// most of the trial balance stays available for Brand-Perception prompts. These
// are persisted as per-brand caps at onboarding finish (the user can raise them
// later on the audit pages). Mirrors the Trigger Lambda's per-brand cap clamp.
const ONBOARDING_WEBSITE_PAGES = 30;
const ONBOARDING_EM_FETCH = 90;

export default function BrandOnboarding() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setActiveBrand, refreshBrands } = useBrand();

  // brandId === 'new' → no brand exists yet; show the create-brand pre-step.
  if (!brandId) {
    return (
      <CenteredMessage>
        <Alert severity="error">{t('onboarding.noBrandSpecified')}</Alert>
      </CenteredMessage>
    );
  }

  if (brandId === 'new') {
    return (
      <Box sx={{ minHeight: '100vh', py: 5, backgroundImage: ONBOARDING_BG }}>
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box component="img" src="/traaker-logo-compact.png" alt="Traaker" sx={{ height: 32, mb: 3 }} />
            <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
              {t('onboarding.create.addBrandTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {t('onboarding.create.addBrandSubtitle')}
            </Typography>
          </Box>
          <CreateBrandStep
            onCreated={async (id) => {
              await refreshBrands();
              navigate(`/onboarding/brand/${id}`, { replace: true });
            }}
            onSkipped={async (brand) => {
              await api.post(`/brands/${brand.brandId}/complete-onboarding`, {}).catch(() => void 0);
              await refreshBrands();
              setActiveBrand(brand);
              navigate('/strategy');
            }}
          />
        </Container>
      </Box>
    );
  }

  return <BrandWizard key={brandId} brandId={brandId} />;
}

function BrandWizard({ brandId }: { brandId: string }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { brands, setActiveBrand, refreshBrands } = useBrand();

  const brand = brands.find((b) => b.brandId === brandId) || null;

  // 0 = Brand Knowledge, 1 = GEO Strategy, 2 = Prompts
  const [step, setStep] = useState(0);

  // On a step change, scroll back to the top — otherwise on large screens the
  // next step opens mid-page at the previous scroll offset.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // "Quitter" is only offered if this ISN'T the tenant's only path into the
  // app — i.e. at least one OTHER brand has already completed onboarding.
  // Deleting the tenant's only onboarded brand would otherwise strand the
  // user (OnboardingGate has nothing else to route them to but back here).
  // Also role-gated: DELETE /brands requires owner/admin server-side.
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const canQuit =
    isAdmin && brands.some((b) => b.brandId !== brandId && isOnboardingComplete(b));

  const handleQuit = async () => {
    await api.delete(`/brands/${brandId}`);
    await refreshBrands();
    navigate('/settings?tab=brands');
  };

  return (
    <Box sx={{ minHeight: '100vh', py: 5, backgroundImage: ONBOARDING_BG }}>
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
          <BrandFavicon brandId={brandId} highRes size={56} sx={{ mb: 1.5 }} />
          <Typography variant="h4" fontWeight={800}>
            {t('onboarding.wizard.configTitle', { name: brand?.name || t('onboarding.wizard.yourBrand') })}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {t('onboarding.wizard.configSubtitle')}
          </Typography>
        </Box>

        <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
          {WIZARD_STEP_KEYS.map((labelKey) => (
            <Step key={labelKey}>
              <StepLabel>{t(labelKey)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <KnowledgeStep
            brandId={brandId}
            brandName={brand?.name}
            onDone={async () => {
              await refreshBrands();
              setStep(1);
            }}
            canQuit={canQuit}
            onQuit={handleQuit}
          />
        )}
        {step === 1 && (
          <GeoStep
            brandId={brandId}
            brand={brand}
            onBack={() => setStep(0)}
            onDone={async () => {
              await refreshBrands();
              setStep(2);
            }}
            canQuit={canQuit}
            onQuit={handleQuit}
          />
        )}
        {step === 2 && (
          <PromptsStep
            brandId={brandId}
            brand={brand}
            onBack={() => setStep(1)}
            onFinish={async () => {
              await refreshBrands();
              if (brand) setActiveBrand(brand);
              // `justOnboarded` triggers the one-time dashboard product tour.
              navigate('/', { state: { justOnboarded: true } });
            }}
            canQuit={canQuit}
            onQuit={handleQuit}
          />
        )}
      </Container>
    </Box>
  );
}

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ maxWidth: 480, width: '100%' }}>{children}</Box>
    </Box>
  );
}

// Strategy drafts discovered at the create step, keyed by the freshly-created
// brandId so KnowledgeStep can pick them up instead of re-running /discover.
// Module-scoped (survives the URL-replace navigation into the wizard).
const discoveredStrategyCache = new Map<string, BrandStrategy>();

// ── Pre-step: create the brand ────────────────────────────────────────────────
// Only reached at /onboarding/brand/new (zero-brand tenant or "+ add brand").
// URL-first flow (matches the mockup): enter a domain → Analyze → the LLM
// extracts the brand name + description (editable) → Continue creates the brand
// and carries the discovered strategy forward.

function CreateBrandStep({
  onCreated,
  onSkipped,
}: {
  onCreated: (brandId: string) => Promise<void>;
  onSkipped: (brand: Brand) => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const { user, tenant, setUiLanguage } = useAuth();
  const isAgency = tenant?.tier === 'agency';
  // Prefill from the marketing-funnel brand hint (essai-gratuit form →
  // /signup?brand=… → stash → Onboarding.tsx forwards it as ?brand=). The
  // visitor already typed their site once — don't make them retype it.
  const [searchParams] = useSearchParams();
  const [url, setUrl] = useState(() => searchParams.get('brand') || '');
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [description, setDescription] = useState('');
  const [aliases, setAliases] = useState<string[]>([]);
  const [aliasInput, setAliasInput] = useState('');
  const [strategy, setStrategy] = useState<BrandStrategy>({});
  // Brand working language — the language ALL AI-generated analysis for this
  // brand is produced in. Immutable after creation. Defaults to the LIVE i18n
  // language (what this flow actually renders in): the stored preference and
  // the signup-lang stash have already been folded into it by AuthContext, and
  // the stash is consumed by then — re-deriving via resolveInitialUiLanguage()
  // fell back to navigator.language, defaulting an FR flow on an EN-locale
  // browser to English (real bug, 2026-07-31). Picking here also (re)aligns
  // the UI language (spec: onboarding pick sets both, they can diverge later).
  const [workingLanguage, setWorkingLanguage] = useState<UiLanguage>(
    normalizeUiLanguage(i18n.language),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [confirmSubPath, setConfirmSubPath] = useState(false);

  const addAlias = () => {
    const v = aliasInput.trim();
    if (v && !aliases.includes(v)) setAliases([...aliases, v]);
    setAliasInput('');
  };

  const domainFromUrl = (raw: string): string => {
    try {
      const u = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      return new URL(u).hostname.replace(/^www\./, '');
    } catch {
      return raw.trim();
    }
  };

  // Detect a URL scoped to a sub-path (e.g. site.com/za/en). The audit only
  // analyzes that section — a common source of "why so few pages?" errors — so
  // we surface it and offer a one-click rewrite to the root domain.
  const subPath = (() => {
    const raw = url.trim();
    if (!raw) return '';
    try {
      const u = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      const path = new URL(u).pathname.replace(/\/+$/, '');
      return path && path !== '/' ? path : '';
    } catch {
      return '';
    }
  })();

  const useMainDomain = () => {
    const raw = url.trim();
    try {
      const u = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      const parsed = new URL(u);
      setUrl(`${parsed.protocol}//${parsed.host}`);
    } catch {
      /* leave as-is on parse failure */
    }
  };

  // Ask for confirmation when the URL is scoped to a sub-path (the audit would
  // only cover that section). Confirmed or root-level URLs analyze directly.
  const handleAnalyze = () => {
    if (!url.trim()) return;
    if (subPath) {
      setConfirmSubPath(true);
      return;
    }
    runAnalyze();
  };

  const runAnalyze = async () => {
    if (!url.trim()) return;
    setConfirmSubPath(false);
    setAnalyzing(true);
    setError(null);
    setWarning(null);
    try {
      const res = await api.post<{
        success: boolean;
        name?: string;
        strategy?: BrandStrategy;
        message?: string;
      }>('/brands/discover-url', { url: url.trim(), language: workingLanguage });

      const fallbackDomain = domainFromUrl(url);
      if (res.success) {
        setName(res.name || '');
        setDomain(fallbackDomain);
        setDescription(res.strategy?.description || '');
        setAliases(res.strategy?.aliases || []);
        setStrategy(res.strategy || {});
      } else {
        // Analysis couldn't extract content — let the user fill it by hand.
        setName('');
        setDomain(fallbackDomain);
        setDescription('');
        setAliases([]);
        setStrategy({});
        setWarning(res.message || t('onboarding.create.analyzeFailed'));
      }
      setAnalyzed(true);
    } catch (err: any) {
      setError(err.message || t('onboarding.create.analyzeError'));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleContinue = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await api.post<Brand>('/brands', { name, url: url.trim() || undefined });
      // Carry the (possibly user-edited) description into the discovered
      // strategy draft so KnowledgeStep starts pre-filled without re-analyzing.
      const draft: BrandStrategy = { ...strategy };
      if (description.trim()) draft.description = description.trim();
      if (aliases.length > 0) draft.aliases = aliases;
      // Brand working language is set ONCE here (immutable after). KnowledgeStep
      // PATCHes this draft, persisting it on the brand.
      draft.workingLanguage = workingLanguage;
      discoveredStrategyCache.set(created.brandId, draft);
      // Align the UI language to the chosen working language on first setup
      // (best-effort — a failure just leaves the current UI language).
      if (workingLanguage !== user?.settings?.uiLanguage) {
        setUiLanguage(workingLanguage).catch(() => {});
      }
      await onCreated(created.brandId);
    } catch (err: any) {
      setError(err.message || t('onboarding.create.createError'));
      setSaving(false);
    }
  };

  // Agency-only escape hatch: create the brand, persist the discovered draft
  // as-is (no validation — the user will fill in Brand Knowledge/GEO
  // Strategy/prompts/schedule manually from the Strategy settings page), and
  // skip the rest of the wizard.
  const handleSkip = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await api.post<Brand>('/brands', { name, url: url.trim() || undefined });
      const draft: BrandStrategy = { ...strategy };
      if (description.trim()) draft.description = description.trim();
      if (aliases.length > 0) draft.aliases = aliases;
      draft.workingLanguage = workingLanguage;
      await api.patch(`/brands/${created.brandId}/strategy`, pruneEmpty(draft));
      if (workingLanguage !== user?.settings?.uiLanguage) {
        setUiLanguage(workingLanguage).catch(() => {});
      }
      await onSkipped(created);
    } catch (err: any) {
      setError(err.message || t('onboarding.create.createError'));
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent sx={{ p: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          {t('onboarding.create.website')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && url.trim() && !analyzing) handleAnalyze();
            }}
            fullWidth
            autoFocus
            placeholder={t('onboarding.create.urlPlaceholder')}
            disabled={analyzing}
          />
          <Button
            variant="outlined"
            onClick={handleAnalyze}
            disabled={!url.trim() || analyzing}
            startIcon={
              analyzing ? <CircularProgress size={16} /> : <AutoAwesomeIcon fontSize="small" />
            }
            sx={{ whiteSpace: 'nowrap', px: 3 }}
          >
            {analyzing ? t('onboarding.create.analyzing') : t('onboarding.create.analyze')}
          </Button>
        </Box>

        {subPath && (
          <Alert
            severity="warning"
            sx={{ mt: 1.5 }}
            action={
              <Button color="inherit" size="small" onClick={useMainDomain}>
                {t('onboarding.create.useMainDomain')}
              </Button>
            }
          >
            {t('onboarding.create.subPathWarning', { path: subPath })}
          </Alert>
        )}

        <Dialog open={confirmSubPath} onClose={() => setConfirmSubPath(false)}>
          <DialogTitle>{t('onboarding.create.subPathConfirmTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('onboarding.create.subPathConfirmBody', { path: subPath })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                useMainDomain();
                setConfirmSubPath(false);
              }}
            >
              {t('onboarding.create.useMainDomain')}
            </Button>
            <Button variant="contained" onClick={runAnalyze}>
              {t('onboarding.create.subPathConfirmProceed')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Working language — picked BEFORE analysis so the prefilled fields are
            generated in this language. Distinct from the UI language (tooltip). */}
        <Box sx={{ mt: 2, maxWidth: 320 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>
              {t('onboarding.create.workingLanguage')}
            </Typography>
            <Tooltip title={t('onboarding.create.workingLanguageHint')} arrow>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.active', cursor: 'help' }} />
            </Tooltip>
          </Box>
          <LanguageSelect
            value={workingLanguage}
            onChange={setWorkingLanguage}
            disabled={analyzing}
            fullWidth
          />
        </Box>

        {analyzing && <AnalyzeSkeleton />}

        {analyzed && !analyzing && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
              <DomainLogo domain={domain} size={48} />
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mb: 2 }}
            >
              {t('onboarding.create.brandInfo')}
            </Typography>
            {warning && <Alert severity="info" sx={{ mb: 2 }}>{warning}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {t('onboarding.create.brandName')}
                </Typography>
                <TextField value={name} onChange={(e) => setName(e.target.value)} fullWidth />
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {t('onboarding.create.aliases')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {t('onboarding.create.aliasesHint')}
                </Typography>
                {aliases.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {aliases.map((a, i) => (
                      <Chip
                        key={`${a}-${i}`}
                        label={a}
                        size="small"
                        onDelete={() => setAliases(aliases.filter((_, idx) => idx !== i))}
                      />
                    ))}
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    value={aliasInput}
                    onChange={(e) => setAliasInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addAlias();
                      }
                    }}
                    fullWidth
                    size="small"
                    placeholder={t('onboarding.create.aliasPlaceholder')}
                  />
                  <Button
                    variant="outlined"
                    onClick={addAlias}
                    disabled={!aliasInput.trim()}
                    sx={{ minWidth: 44, px: 0 }}
                  >
                    <AddIcon fontSize="small" />
                  </Button>
                </Box>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {t('onboarding.create.domain')}
                </Typography>
                <TextField
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  fullWidth
                  disabled
                />
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {t('onboarding.create.description')}
                </Typography>
                <TextField
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  minRows={4}
                />
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mt: 3 }}>
          {isAgency && analyzed && !analyzing && (
            <Tooltip title={t('onboarding.create.skipWizardHint')} arrow>
              <Button variant="text" onClick={handleSkip} disabled={!name.trim() || saving}>
                {t('onboarding.create.skipWizard')}
              </Button>
            </Tooltip>
          )}
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={!analyzed || !name.trim() || saving}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : t('onboarding.create.continue')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

// Favicon for a bare domain (used pre-brand-creation, where BrandFavicon's
// brandId doesn't exist yet). Falls back to a storefront glyph.
function DomainLogo({ domain, size = 48 }: { domain: string; size?: number }) {
  const src = useDomainFavicon(domain || undefined, 64);
  return (
    <Avatar
      src={src || undefined}
      variant="rounded"
      sx={{ width: size, height: size, bgcolor: 'action.hover', color: 'text.secondary' }}
    >
      <StorefrontIcon sx={{ fontSize: size * 0.6 }} />
    </Avatar>
  );
}

// Grey placeholder bars shown while /discover-url runs (matches the mockup).
function AnalyzeSkeleton() {
  const bar = (w: string | number, h = 16) => (
    <Box sx={{ width: w, height: h, borderRadius: 1, bgcolor: 'action.hover' }} />
  );
  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {bar('40%', 1)}
        {bar(80, 12)}
        {bar('40%', 1)}
      </Box>
      {bar('30%', 12)}
      {bar('100%', 40)}
      {bar('25%', 12)}
      {bar('100%', 40)}
      {bar('35%', 12)}
      {bar('100%', 90)}
    </Box>
  );
}

// ── Part 1: Brand Knowledge ──────────────────────────────────────────────────
// Onboarding-simplified: a typewriter intro, then three compact sub-screens
// (activity / authority topics / use-cases). Fields not surfaced here
// (description, personas, customerQuestions, objections, avoidTopics) are kept
// in the draft untouched so they survive to the full Brand Knowledge editor.
// Locations + languages are handled later, in the Prompts step.

// Typewriter copy as styled segments — `hl` segments get the brand colour +
// bold + a slightly larger size, so key words pop as they're typed.
type TypeSegment = { t: string; hl?: boolean };

// Typewriter intro segments are a single sentence intentionally split so key
// words get the highlight styling. The highlight boundaries live in code; each
// fragment's text is sourced from i18n. Builder functions resolve at render.
type TFn = (key: string) => string;

const introSegments = (t: TFn): TypeSegment[] => [
  { t: t('onboarding.knowledge.intro.p1') },
  { t: t('onboarding.knowledge.intro.hlBrand'), hl: true },
  { t: t('onboarding.knowledge.intro.p2') },
  { t: t('onboarding.knowledge.intro.hlDna'), hl: true },
  { t: t('onboarding.knowledge.intro.p3') },
  { t: t('onboarding.knowledge.intro.hlStrategy'), hl: true },
  { t: t('onboarding.knowledge.intro.p4') },
  { t: t('onboarding.knowledge.intro.hlEngines'), hl: true },
  { t: t('onboarding.knowledge.intro.p5') },
];

const promptsGeneratingSegments = (t: TFn): TypeSegment[] => [
  { t: t('onboarding.prompts.generating.p1') },
  { t: t('onboarding.prompts.generating.hlPrompts'), hl: true },
  { t: t('onboarding.prompts.generating.p2') },
  { t: t('onboarding.prompts.generating.hlQuestions'), hl: true },
  { t: t('onboarding.prompts.generating.p3') },
  { t: t('onboarding.prompts.generating.hlAi'), hl: true },
  { t: t('onboarding.prompts.generating.p4') },
];

// Shared explainer for the L1→L5 ladder is resolved via
// `onboarding.prompts.review.levelLadderTooltip` at the render site.

// Industry chips = the standardized `IndustryCode` enum (labels from shared).
// No "Autre" — the enum is the cache key for the cross-tenant authority list.
const INDUSTRY_OPTIONS = INDUSTRY_CODES;

// Map a free-text industry (from discovery, e.g. "SaaS", "logiciel B2B",
// "cabinet de conseil") to an IndustryCode. Aliases fold accents + cover common
// English/French phrasings so the discovered draft snaps to the right chip.
const INDUSTRY_ALIASES: Partial<Record<IndustryCode, string[]>> = {
  saas_b2b: ['saas', 'logiciel', 'software', 'application', 'app', 'plateforme', 'platform', 'b2b'],
  ecommerce: ['e-commerce', 'ecommerce', 'commerce en ligne', 'boutique en ligne', 'vente en ligne', 'dropshipping'],
  retail: ['retail', 'commerce', 'magasin', 'boutique', 'distribution', 'grande distribution'],
  restauration: ['restauration', 'restaurant', 'food', 'traiteur', 'cafe', 'bar'],
  tourism_hospitality: ['hotellerie', 'hotel', 'hospitality', 'tourisme', 'voyage', 'gite', 'camping'],
  health: ['sante', 'health', 'medical', 'medecine', 'pharma', 'biotech', 'clinique', 'bien-etre'],
  legal_services: ['juridique', 'avocat', 'legal', 'droit', 'notaire', 'cabinet juridique', 'legaltech'],
  real_estate: ['immobilier', 'real estate', 'proptech', 'foncier', 'agence immobiliere'],
  manufacturing: ['industrie', 'manufacturing', 'fabrication', 'usine', 'production', 'industriel'],
  construction: ['construction', 'btp', 'batiment', 'travaux', 'renovation', 'architecte'],
  finance_banking: ['finance', 'banque', 'bank', 'fintech', 'investissement', 'credit', 'comptabilite'],
  insurance: ['assurance', 'insurance', 'assurtech', 'mutuelle', 'courtier'],
  media_publishing: ['media', 'presse', 'edition', 'publishing', 'journal', 'magazine'],
  marketing_agency: ['marketing', 'agence', 'agency', 'communication', 'publicite', 'advertising', 'seo', 'growth'],
  education_training: ['education', 'formation', 'training', 'edtech', 'ecole', 'school', 'apprentissage', 'universite'],
  energy: ['energie', 'energy', 'renouvelable', 'solaire', 'eolien', 'petrole', 'gaz', 'cleantech'],
  transport_logistics: ['transport', 'logistique', 'logistics', 'livraison', 'fret', 'supply chain', 'mobilite'],
  automotive: ['automobile', 'auto', 'automotive', 'voiture', 'garage', 'concessionnaire'],
  fashion_beauty: ['mode', 'fashion', 'beaute', 'beauty', 'cosmetique', 'vetement', 'luxe'],
  sports_leisure: ['sport', 'loisirs', 'fitness', 'salle de sport', 'jeu', 'gaming', 'divertissement'],
  b2b_services: ['services aux entreprises', 'conseil', 'consulting', 'cabinet', 'b2b services', 'rh', 'recrutement'],
  b2c_services: ['services aux particuliers', 'services a la personne', 'artisan', 'depannage', 'menage'],
  consumer_tech: ['tech', 'technologie', 'technology', 'hardware', 'iot', 'electronique', 'gadget', 'objets connectes'],
  public_sector: ['secteur public', 'public', 'administration', 'collectivite', 'gouvernement', 'ong', 'association'],
};

function matchIndustry(raw: string): IndustryCode | null {
  const needle = foldLower(raw).trim();
  if (!needle) return null;
  // Exact code or French label match first (accent/case-insensitive).
  const byCode = INDUSTRY_CODES.find((c) => c === needle);
  if (byCode) return byCode;
  const byLabel = INDUSTRY_CODES.find(
    (c) => foldLower(INDUSTRY_LABELS_FR[c]) === needle || foldLower(INDUSTRY_LABELS_EN[c]) === needle,
  );
  if (byLabel) return byLabel;
  // Synonym/substring match — both directions so "saas b2b" and "logiciel" hit.
  for (const code of INDUSTRY_CODES) {
    const aliases = INDUSTRY_ALIASES[code] || [];
    if (aliases.some((a) => needle.includes(a) || a.includes(needle))) return code;
  }
  return null;
}

// `labelKey` resolved with `t()` at render. B2B/B2C stay verbatim (proper terms).
const BUSINESS_MODELS: { value: 'b2b' | 'b2c' | 'both'; labelKey: string }[] = [
  { value: 'b2b', labelKey: 'onboarding.knowledge.businessModel.b2b' },
  { value: 'b2c', labelKey: 'onboarding.knowledge.businessModel.b2c' },
  { value: 'both', labelKey: 'onboarding.knowledge.businessModel.both' },
];

// Envergure ladder for the slider (index → value). `labelKey` resolved at render.
const ENVERGURE_STEPS: { value: Envergure; labelKey: string }[] = [
  { value: 'hyper-local', labelKey: 'onboarding.knowledge.envergure.hyperLocal' },
  { value: 'multi-local', labelKey: 'onboarding.knowledge.envergure.multiLocal' },
  { value: 'mono-country', labelKey: 'onboarding.knowledge.envergure.national' },
  { value: 'multi-country', labelKey: 'onboarding.knowledge.envergure.multiCountry' },
  { value: 'global', labelKey: 'onboarding.knowledge.envergure.global' },
];

const MAX_AUTHORITY_TOPICS = 5;
// Pre-select this many of the ~10 proposed topics; the user picks up to MAX.
const DEFAULT_AUTHORITY_PRESELECT = 3;

// Keyword → Material icon. First matching keyword (substring, accent-insensitive)
// wins; falls back to a varied default by index so non-matching topics differ.
const TOPIC_ICON_RULES: { kw: string[]; icon: SvgIconComponent }[] = [
  { kw: ['immobil', 'logement', 'propriété', 'propriete', 'location', 'foncier', 'copropri', 'syndic', 'maison', 'habitat', 'real estate', 'housing'], icon: HomeWorkIcon },
  { kw: ['notaire', 'juridiq', 'droit', 'legal', 'loi', 'réglement', 'reglement', 'conformité', 'conformite', 'rgpd', 'contrat'], icon: GavelIcon },
  { kw: ['banque', 'bancaire', 'finance', 'financ', 'crédit', 'credit', 'prêt', 'pret', 'ptz', 'investiss', 'patrimoine'], icon: AccountBalanceIcon },
  { kw: ['épargne', 'epargne', 'saving', 'économie', 'economie', 'budget'], icon: SavingsIcon },
  { kw: ['impôt', 'impot', 'taxe', 'fiscal', 'tva', 'exonération', 'exoneration', 'facture'], icon: ReceiptLongIcon },
  { kw: ['vente', 'commercial', 'partenaire', 'négoci', 'negoci', 'deal', 'b2b'], icon: HandshakeIcon },
  { kw: ['ingénier', 'ingenier', 'engineering', 'technique', 'mécaniq', 'mecaniq', 'industri', 'production', 'usine'], icon: EngineeringIcon },
  { kw: ['recherche', 'r&d', 'science', 'laborat', 'innovation tech', 'biotech', 'pharma', 'chimie'], icon: ScienceIcon },
  { kw: ['santé', 'sante', 'health', 'médic', 'medic', 'soin', 'sécurité', 'securite', 'safety'], icon: HealthAndSafetyIcon },
  { kw: ['écolog', 'ecolog', 'durable', 'environnement', 'green', 'rse', 'carbone', 'éco', 'eco'], icon: EcoIcon },
  { kw: ['énergie', 'energie', 'energy', 'électr', 'electr', 'solaire', 'renouvelable'], icon: BoltIcon },
  { kw: ['hardware', 'composant', 'puce', 'semi-conduc', 'électroniq', 'electroniq', 'matériel info'], icon: MemoryIcon },
  { kw: ['cloud', 'saas', 'hébergement', 'hebergement', 'serveur', 'infrastructure'], icon: CloudIcon },
  { kw: ['sécurité info', 'cyber', 'sécuris', 'securis', 'protection données', 'authentif'], icon: SecurityIcon },
  { kw: ['marketing', 'communication', 'publicité', 'publicite', 'notoriété', 'notoriete', 'marque', 'branding'], icon: CampaignIcon },
  { kw: ['formation', 'éducation', 'education', 'apprentiss', 'école', 'ecole', 'cours', 'pédagog', 'pedagog'], icon: SchoolIcon },
  { kw: ['logistiq', 'livraison', 'transport', 'supply', 'expédition', 'expedition', 'fret'], icon: LocalShippingIcon },
  { kw: ['commerce', 'e-commerce', 'boutique', 'retail', 'magasin', 'vente en ligne'], icon: StorefrontIcon },
  { kw: ['support', 'service client', 'assistance', 'sav', 'relation client'], icon: SupportAgentIcon },
  { kw: ['data', 'donnée', 'donnee', 'analyt', 'statistiq', 'mesure', 'reporting', 'bi'], icon: InsightsIcon },
  { kw: ['architect', 'construc', 'bâtiment', 'batiment', 'btp', 'travaux', 'matériaux', 'materiaux', 'rénovation', 'renovation'], icon: ArchitectureIcon },
  { kw: ['équipe', 'equipe', 'rh', 'recrutement', 'talent', 'collabor', 'management', 'human'], icon: GroupsIcon },
  { kw: ['international', 'export', 'mondial', 'global', 'voyage', 'tourism'], icon: TravelExploreIcon },
  { kw: ['croissance', 'growth', 'développement', 'developpement', 'performance', 'roi'], icon: TrendingUpIcon },
];
// Varied fallbacks so unmatched topics still differ from one another.
const TOPIC_ICON_FALLBACKS: SvgIconComponent[] = [
  LightbulbIcon, CategoryIcon, InsightsIcon, RocketLaunchIcon, PublicIcon, TrendingUpIcon,
];

function foldLower(s: string): string {
  return (s || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

function pickTopicIcon(topic: string, index: number): SvgIconComponent {
  const t = foldLower(topic);
  for (const rule of TOPIC_ICON_RULES) {
    if (rule.kw.some((k) => t.includes(foldLower(k)))) return rule.icon;
  }
  return TOPIC_ICON_FALLBACKS[index % TOPIC_ICON_FALLBACKS.length];
}

const MAX_USECASES = 5;
// Max GEO-suggested competitors shown in onboarding. Pre-selection is further
// bounded by the tier `competitors` cap (see the seed effect).
const COMPETITOR_PROPOSE_MAX = 5;

// Audience archetypes — a funnel from unaware to deliberating. Drives prompt
// PHRASING at generation time (mostly referral + authority). Defaults are a
// sensible mid-funnel-weighted starting point the user adjusts via sliders.
const DEFAULT_AUDIENCE_MIX: AudienceMix = {
  problemAware: 20,
  recommendation: 20,
  informed: 30,
  evaluative: 30,
};

// The 4 archetypes ordered as a funnel (unaware → deliberating). The audience UI
// is a SINGLE slider over this axis; its position maps to a peaked 4-way mix
// (the selected profile + spillover to neighbours) so the backend `audienceMix`
// shape is unchanged. Slider value 0..100 → 4 equal funnel bands.
const AUDIENCE_FUNNEL: AudienceArchetype[] = [
  'problemAware',
  'recommendation',
  'informed',
  'evaluative',
];
// i18n keys resolved with `t()` at render.
const AUDIENCE_LABEL_KEYS: Record<AudienceArchetype, string> = {
  problemAware: 'onboarding.knowledge.audience.problemAware.label',
  recommendation: 'onboarding.knowledge.audience.recommendation.label',
  informed: 'onboarding.knowledge.audience.informed.label',
  evaluative: 'onboarding.knowledge.audience.evaluative.label',
};
const AUDIENCE_DESCRIPTION_KEYS: Record<AudienceArchetype, string> = {
  problemAware: 'onboarding.knowledge.audience.problemAware.description',
  recommendation: 'onboarding.knowledge.audience.recommendation.description',
  informed: 'onboarding.knowledge.audience.informed.description',
  evaluative: 'onboarding.knowledge.audience.evaluative.description',
};
// Concrete example of the kind of question this prospect types — makes the
// abstract "mindset" tangible for the user choosing on the slider.
const AUDIENCE_EXAMPLE_KEYS: Record<AudienceArchetype, string> = {
  problemAware: 'onboarding.knowledge.audience.problemAware.example',
  recommendation: 'onboarding.knowledge.audience.recommendation.example',
  informed: 'onboarding.knowledge.audience.informed.example',
  evaluative: 'onboarding.knowledge.audience.evaluative.example',
};
// Short bookend labels for the slider ends (the journey, not a segment).
const AUDIENCE_START_LABEL_KEY = 'onboarding.knowledge.audience.startLabel';
const AUDIENCE_END_LABEL_KEY = 'onboarding.knowledge.audience.endLabel';

// Slider position (0..100) → index of the dominant funnel band.
function audiencePosToIndex(pos: number): number {
  return Math.min(AUDIENCE_FUNNEL.length - 1, Math.floor((pos / 100) * AUDIENCE_FUNNEL.length));
}

// Dominant index → peaked 4-way mix summing to 100 (60% on the chosen profile,
// the rest split across the immediate neighbours). Keeps generation phrasing
// centered on the selected mindset without zeroing out the others entirely.
function audienceIndexToMix(idx: number): AudienceMix {
  const weights: number[] = AUDIENCE_FUNNEL.map((_, i) => {
    const d = Math.abs(i - idx);
    if (d === 0) return 3;
    if (d === 1) return 1;
    return 0;
  });
  const total = weights.reduce((s, w) => s + w, 0);
  const mix = {} as AudienceMix;
  let allocated = 0;
  AUDIENCE_FUNNEL.forEach((key, i) => {
    const v =
      i === AUDIENCE_FUNNEL.length - 1
        ? 100 - allocated
        : Math.round((weights[i] / total) * 100);
    mix[key] = Math.max(0, v);
    allocated += mix[key];
  });
  return mix;
}

// Recover a slider position from a stored mix (the dominant band → its centre).
function audienceMixToPos(mix: AudienceMix): number {
  let bestIdx = 0;
  let best = -1;
  AUDIENCE_FUNNEL.forEach((key, i) => {
    if (mix[key] > best) {
      best = mix[key];
      bestIdx = i;
    }
  });
  return Math.round(((bestIdx + 0.5) / AUDIENCE_FUNNEL.length) * 100);
}

// Typewriter intro — types `text` character by character. We render the FULL
// text always (so layout never reflows) and only move the caret. Skippable by
// click. When `onDone` is provided it fires ~900ms after typing completes (and
// the card is clickable to skip); when omitted, the text just stays typed —
// used as a busy/loading screen whose completion is driven externally.
function TypewriterIntro({
  segments,
  onDone,
  speedMs = 45,
}: {
  segments?: TypeSegment[];
  onDone?: () => void;
  speedMs?: number;
}) {
  const { t } = useTranslation();
  const segs = segments ?? introSegments(t);
  const full = segs.map((s) => s.t).join('');
  const [count, setCount] = useState(0);
  const done = useRef(false);

  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setCount(i);
      if (i >= full.length) {
        clearInterval(id);
        if (onDone && !done.current) {
          done.current = true;
          setTimeout(onDone, 900);
        }
      }
    }, speedMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [full, speedMs]);

  const skip = () => {
    if (!onDone || done.current) return;
    done.current = true;
    setCount(full.length);
    onDone();
  };

  // Render each segment, slicing the reveal boundary within it. Highlighted
  // segments get the brand colour + bold + a larger size. The full text is
  // always present (un-typed part at opacity 0) so the paragraph never reflows.
  let consumed = 0;
  return (
    <Card sx={{ cursor: onDone ? 'pointer' : 'default' }} onClick={skip}>
      <CardContent sx={{ p: 5, minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 500, lineHeight: 1.7, textAlign: 'center', maxWidth: 660 }}
        >
          {segs.map((seg, i) => {
            const start = consumed;
            consumed += seg.t.length;
            const shownChars = Math.max(0, Math.min(seg.t.length, count - start));
            const visible = seg.t.slice(0, shownChars);
            const hidden = seg.t.slice(shownChars);
            // The caret sits in the segment where the reveal boundary currently
            // falls (typing in progress within this segment).
            const caretHere = count >= start && count < start + seg.t.length;
            return (
              <Box
                component="span"
                key={i}
                sx={
                  seg.hl
                    ? { color: 'primary.main', fontWeight: 800, fontSize: '1.12em' }
                    : undefined
                }
              >
                <Box component="span">{visible}</Box>
                {caretHere && (
                  <Box
                    component="span"
                    sx={{
                      opacity: 0.6,
                      animation: 'blink 1s step-end infinite',
                      '@keyframes blink': { '50%': { opacity: 0 } },
                    }}
                  >
                    |
                  </Box>
                )}
                <Box component="span" sx={{ opacity: 0 }}>
                  {hidden}
                </Box>
              </Box>
            );
          })}
        </Typography>
      </CardContent>
    </Card>
  );
}

// A selectable proposal pill (pre-checked, toggleable) — used for use-cases.
function ProposalPill({
  label,
  icon: PillIcon,
  selected,
  disabled,
  onToggle,
}: {
  label: string;
  icon: SvgIconComponent;
  selected: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <Box
      onClick={() => {
        if (!disabled || selected) onToggle();
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1,
        borderRadius: 2,
        cursor: disabled && !selected ? 'not-allowed' : 'pointer',
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'primary.50' : 'transparent',
        opacity: disabled && !selected ? 0.45 : 1,
        transition: 'all 0.15s',
      }}
    >
      <PillIcon sx={{ fontSize: 20, color: selected ? 'primary.main' : 'action.active' }} />
      <Typography variant="body2" sx={{ flexGrow: 1 }}>
        {label}
      </Typography>
      {selected && <CheckIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
    </Box>
  );
}

// Shared "Quitter" control for the wizard steps — deletes the brand being
// onboarded and returns to Settings → Marques. Only rendered when the parent
// determines `canQuit` (not the tenant's only path into the app, admin role).
function QuitOnboardingButton({ brandName, onQuit }: { brandName?: string; onQuit: () => Promise<void> }) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [quitting, setQuitting] = useState(false);

  const handleConfirm = async () => {
    setQuitting(true);
    try {
      await onQuit();
    } finally {
      setQuitting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Button variant="text" onClick={() => setConfirmOpen(true)}>
        {t('onboarding.common.quit')}
      </Button>
      <Dialog open={confirmOpen} onClose={() => !quitting && setConfirmOpen(false)}>
        <DialogTitle>{t('onboarding.common.quitConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('onboarding.common.quitConfirmBody', { name: brandName || t('onboarding.wizard.yourBrand') })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={quitting}>
            {t('onboarding.common.back')}
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirm} disabled={quitting}>
            {quitting ? <CircularProgress size={20} color="inherit" /> : t('onboarding.common.quitConfirmProceed')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function KnowledgeStep({
  brandId,
  brandName,
  onDone,
  canQuit,
  onQuit,
}: {
  brandId: string;
  brandName?: string;
  onDone: () => Promise<void>;
  canQuit: boolean;
  onQuit: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const industryLabelMap = industryLabels(i18n.language);
  // Sub-screens: 'intro' → 'activity' → 'authority' → 'usecases'
  const [screen, setScreen] = useState<'loading' | 'intro' | 'activity' | 'authority' | 'usecases'>('loading');
  const [draft, setDraft] = useState<BrandStrategy>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Scroll to top when moving between sub-screens (large screens otherwise keep
  // the previous scroll offset on "Continuer").
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [screen]);

  // Editable controls (seeded from the discovered draft).
  const [industry, setIndustry] = useState<IndustryCode | ''>('');
  const [businessModel, setBusinessModel] = useState<'b2b' | 'b2c' | 'both' | ''>('');
  const [envergureIdx, setEnvergureIdx] = useState(2); // default 'mono-country'

  // Authority topics (keywords) — selected set, max 5.
  const [topicPool, setTopicPool] = useState<string[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [topicInput, setTopicInput] = useState('');

  // Use-cases (scenarios) — selected set.
  const [scenarioPool, setScenarioPool] = useState<string[]>([]);
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);
  const [scenarioInput, setScenarioInput] = useState('');

  // Audience distribution — phrasing bias for query generation (sums to 100).
  const [audienceMix, setAudienceMix] = useState<AudienceMix>(DEFAULT_AUDIENCE_MIX);

  const seedFromDraft = (s: BrandStrategy) => {
    setDraft(s);
    // Industry: snap the discovered free-text (or an already-migrated code) to an
    // IndustryCode. No fallback bucket — an unmatched value leaves the picker empty
    // so the user explicitly chooses (the enum is the authority-cache key).
    if (s.industry) {
      const known = matchIndustry(s.industry as string);
      if (known) setIndustry(known);
    }
    if (s.businessModel) setBusinessModel(s.businessModel);
    if (s.envergure) {
      const idx = ENVERGURE_STEPS.findIndex((e) => e.value === s.envergure);
      if (idx >= 0) setEnvergureIdx(idx);
    }
    const kws = (s.keywords || []).slice(0, 10);
    setTopicPool(kws);
    setSelectedTopics(kws.slice(0, DEFAULT_AUTHORITY_PRESELECT));
    const scs = (s.scenarios || []).slice(0, 10);
    setScenarioPool(scs);
    setSelectedScenarios(scs.slice(0, MAX_USECASES));
    if (s.audienceMix) setAudienceMix(s.audienceMix);
  };

  // Load discovered draft (from create-step cache) or run /discover.
  useEffect(() => {
    let cancelled = false;
    const cached = discoveredStrategyCache.get(brandId);
    if (cached) {
      discoveredStrategyCache.delete(brandId);
      seedFromDraft(cached);
      setScreen('intro');
      return;
    }
    setScreen('loading');
    api
      .post<{ success: boolean; message?: string; strategy?: BrandStrategy }>(
        `/brands/${brandId}/discover`,
        {},
      )
      .then((res) => {
        if (cancelled) return;
        seedFromDraft(res.strategy || {});
        setScreen('intro');
      })
      .catch(() => {
        if (cancelled) return;
        seedFromDraft({});
        setScreen('intro');
      });
    return () => {
      cancelled = true;
    };
  }, [brandId]);

  const toggleTopic = (t: string) => {
    setSelectedTopics((prev) => {
      if (prev.includes(t)) return prev.filter((x) => x !== t);
      if (prev.length >= MAX_AUTHORITY_TOPICS) return prev; // cap at 5
      return [...prev, t];
    });
  };

  const addTopic = () => {
    const v = topicInput.trim();
    if (v && !topicPool.includes(v)) {
      setTopicPool([...topicPool, v]);
      if (selectedTopics.length < MAX_AUTHORITY_TOPICS) {
        setSelectedTopics([...selectedTopics, v]);
      }
    }
    setTopicInput('');
  };

  const toggleScenario = (s: string) => {
    setSelectedScenarios((prev) => {
      if (prev.includes(s)) return prev.filter((x) => x !== s);
      if (prev.length >= MAX_USECASES) return prev; // cap at 5
      return [...prev, s];
    });
  };

  const addScenario = () => {
    const v = scenarioInput.trim();
    if (v && !scenarioPool.includes(v)) {
      setScenarioPool([...scenarioPool, v]);
      if (selectedScenarios.length < MAX_USECASES) {
        setSelectedScenarios([...selectedScenarios, v]);
      }
    }
    setScenarioInput('');
  };

  // Final save: merge edited fields into the discovered draft (preserving the
  // hidden fields), PATCH strategy, then validate.
  const handleFinish = async () => {
    setSaving(true);
    setError(null);
    // `draft.targetLanguages` may carry the discovery LLM's inferred languages
    // (e.g. ["fr","en"]) — this step has no language UI and never lets the
    // user review/adjust it, yet spreading it in would silently smuggle it
    // into the strategy PATCH, tripping the tenant-wide `languages` quota gate
    // on a screen ("Cas d'usage") that has nothing to do with languages. The
    // Prompts step owns `targetLanguages` (its own quota-aware picker) and
    // patches it explicitly later — strip it here so this save never touches it.
    const { targetLanguages: _discoveredLanguages, ...draftWithoutLanguages } = draft;
    const merged: BrandStrategy = {
      ...draftWithoutLanguages,
      ...(industry && { industry }),
      ...(businessModel && { businessModel }),
      envergure: ENVERGURE_STEPS[envergureIdx].value,
      ...(selectedTopics.length > 0 && { keywords: selectedTopics }),
      ...(selectedScenarios.length > 0 && { scenarios: selectedScenarios }),
      audienceMix,
    };
    try {
      await api.patch(`/brands/${brandId}/strategy`, pruneEmpty(merged));
      await api.post(`/brands/${brandId}/strategy/validate`, {});
      await onDone();
    } catch (err: any) {
      setError(err.message || t('onboarding.knowledge.saveError'));
      setSaving(false);
    }
  };

  if (screen === 'loading') {
    return (
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            {t('onboarding.knowledge.analyzing', { name: brandName || t('onboarding.knowledge.theBrand') })}
          </Typography>
          <LinearProgress sx={{ borderRadius: 1 }} />
        </CardContent>
      </Card>
    );
  }

  if (screen === 'intro') {
    return <TypewriterIntro onDone={() => setScreen('activity')} />;
  }

  // ── Sub-screen: Activity ──
  if (screen === 'activity') {
    return (
      <Card>
        <CardContent sx={{ p: 4 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
            {t('onboarding.knowledge.activity.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('onboarding.knowledge.activity.subtitle')}
          </Typography>

          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            {t('onboarding.knowledge.activity.industry')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {INDUSTRY_OPTIONS.map((code) => (
              <Chip
                key={code}
                label={industryLabelMap[code]}
                onClick={() => setIndustry(code)}
                color={industry === code ? 'primary' : 'default'}
                variant={industry === code ? 'filled' : 'outlined'}
              />
            ))}
          </Box>

          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            {t('onboarding.knowledge.activity.targetMarket')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            {BUSINESS_MODELS.map((m) => (
              <Chip
                key={m.value}
                label={t(m.labelKey)}
                onClick={() => setBusinessModel(m.value)}
                color={businessModel === m.value ? 'primary' : 'default'}
                variant={businessModel === m.value ? 'filled' : 'outlined'}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PublicIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={600}>
              {t('onboarding.knowledge.activity.geoScope')}
            </Typography>
            <Tooltip
              title={t('onboarding.knowledge.activity.geoScopeTooltip')}
              arrow
            >
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.active', cursor: 'help' }} />
            </Tooltip>
          </Box>
          <Box sx={{ px: 1 }}>
            <Slider
              value={envergureIdx}
              onChange={(_, v) => setEnvergureIdx(v as number)}
              min={0}
              max={ENVERGURE_STEPS.length - 1}
              step={1}
              marks={ENVERGURE_STEPS.map((e, i) => ({ value: i, label: t(e.labelKey) }))}
              sx={{
                '& .MuiSlider-markLabel': { fontSize: 11 },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            {canQuit ? <QuitOnboardingButton brandName={brandName} onQuit={onQuit} /> : <span />}
            <Button variant="contained" onClick={() => setScreen('authority')}>
              {t('onboarding.common.continue')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // ── Sub-screen: Authority topics ──
  if (screen === 'authority') {
    return (
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
            {t('onboarding.knowledge.authority.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('onboarding.knowledge.authority.description', { max: MAX_AUTHORITY_TOPICS })}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {t('onboarding.knowledge.authority.selectedCount', { n: selectedTopics.length, max: MAX_AUTHORITY_TOPICS })}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1.5, mb: 2 }}>
            {topicPool.map((t, i) => {
              const selected = selectedTopics.includes(t);
              const TopicIcon = pickTopicIcon(t, i);
              const atMax = !selected && selectedTopics.length >= MAX_AUTHORITY_TOPICS;
              return (
                <Box
                  key={`${t}-${i}`}
                  onClick={() => {
                    if (!atMax) toggleTopic(t);
                  }}
                  sx={{
                    position: 'relative',
                    minHeight: 112,
                    p: 1.5,
                    borderRadius: 3,
                    cursor: atMax ? 'not-allowed' : 'pointer',
                    bgcolor: selected ? 'primary.50' : 'background.paper',
                    border: '1px solid',
                    borderColor: selected ? 'primary.main' : 'divider',
                    boxShadow: selected
                      ? '0 2px 10px rgba(124,58,237,0.18)'
                      : '0 1px 2px rgba(0,0,0,0.03)',
                    opacity: atMax ? 0.45 : 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    gap: 1,
                    transition: 'all 0.15s',
                    '&:hover': atMax
                      ? undefined
                      : { borderColor: 'primary.main', boxShadow: '0 2px 10px rgba(124,58,237,0.12)' },
                  }}
                >
                  {selected && (
                    <CheckCircleIcon
                      sx={{ position: 'absolute', top: 8, right: 8, fontSize: 18, color: 'primary.main' }}
                    />
                  )}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: selected ? 'primary.main' : 'action.hover',
                      transition: 'all 0.15s',
                    }}
                  >
                    <TopicIcon sx={{ fontSize: 24, color: selected ? '#fff' : 'primary.main' }} />
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: 'text.primary', lineHeight: 1.25 }}
                  >
                    {t}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              value={topicInput}
              onChange={(e) => setTopicInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTopic();
                }
              }}
              fullWidth
              size="small"
              placeholder={t('onboarding.knowledge.authority.addPlaceholder')}
              inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.keywords.maxCharsPerItem }}
            />
            <Button variant="outlined" onClick={addTopic} disabled={!topicInput.trim()} sx={{ minWidth: 44, px: 0 }}>
              <AddIcon fontSize="small" />
            </Button>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => setScreen('activity')}>{t('onboarding.common.back')}</Button>
              {canQuit && <QuitOnboardingButton brandName={brandName} onQuit={onQuit} />}
            </Box>
            <Button variant="contained" onClick={() => setScreen('usecases')} disabled={selectedTopics.length === 0}>
              {t('onboarding.common.continue')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // ── Sub-screen: Use-cases ──
  return (
    <Card>
      <CardContent sx={{ p: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
          {t('onboarding.knowledge.usecases.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('onboarding.knowledge.usecases.description', { max: MAX_USECASES })}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {t('onboarding.knowledge.usecases.selectedCount', { n: selectedScenarios.length, max: MAX_USECASES })}
        </Typography>

        {scenarioPool.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('onboarding.knowledge.usecases.noneDetected')}
          </Alert>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
              mb: 2,
            }}
          >
            {scenarioPool.map((s, i) => {
              const selected = selectedScenarios.includes(s);
              return (
                <ProposalPill
                  key={`${s}-${i}`}
                  label={s}
                  icon={pickTopicIcon(s, i)}
                  selected={selected}
                  disabled={!selected && selectedScenarios.length >= MAX_USECASES}
                  onToggle={() => toggleScenario(s)}
                />
              );
            })}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            value={scenarioInput}
            onChange={(e) => setScenarioInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addScenario();
              }
            }}
            fullWidth
            size="small"
            placeholder={t('onboarding.knowledge.usecases.addPlaceholder')}
          />
          <Button variant="outlined" onClick={addScenario} disabled={!scenarioInput.trim()} sx={{ minWidth: 44, px: 0 }}>
            <AddIcon fontSize="small" />
          </Button>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            {t('onboarding.knowledge.audience.question')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('onboarding.knowledge.audience.hint')}
          </Typography>
          <AudienceFunnelSlider mix={audienceMix} onChange={setAudienceMix} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setScreen('authority')} disabled={saving}>
              {t('onboarding.common.back')}
            </Button>
            {canQuit && <QuitOnboardingButton brandName={brandName} onQuit={onQuit} />}
          </Box>
          <Button variant="contained" onClick={handleFinish} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : t('onboarding.common.validateContinue')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}


// ── Part 2: GEO Strategy ──────────────────────────────────────────────────────
// Generate on entry (if absent), let the user review, then validate.

function GeoStep({
  brandId,
  brand,
  onBack,
  onDone,
  canQuit,
  onQuit,
}: {
  brandId: string;
  brand: Brand | null;
  onBack: () => void;
  onDone: () => Promise<void>;
  canQuit: boolean;
  onQuit: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { refreshBrands } = useBrand();
  const { tenant } = useAuth();
  // Competitors entitlement (tier + packs + override, ceiling-clamped). Only
  // TRACKED competitors (c.track) are added to analysis, so they're what counts.
  const competitorLimit = tenant
    ? resolveEntitlement('competitors', tenant.tier, {
        override: tenant.settings?.quotaOverrides?.competitors,
        packs: tenant.settings?.activePacks?.competitors,
      }).limit
    : -1;
  const [generating, setGenerating] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const geo = brand?.geoStrategy;
  const hasGeo = geo && Object.keys(geo).length > 0;

  // Authority sources: candidate list from GEO, with a selected subset.
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  // Resolved domain per source name (for favicons) — best-effort, keyed by name.
  const [sourceDomains, setSourceDomains] = useState<Record<string, string>>({});
  // Competitors: resolved domain + whether to track in Competitor Analysis.
  const [competitors, setCompetitors] = useState<
    { name: string; domain: string | null; track: boolean }[]
  >([]);
  const [competitorInput, setCompetitorInput] = useState('');
  // Example queries table (one Nemotron call).
  const [examples, setExamples] = useState<QueryExampleRow[]>([]);
  const [examplesLoading, setExamplesLoading] = useState(false);
  // Key phrases of the strategic angle to highlight (verbatim substrings).
  const [anglePhrases, setAnglePhrases] = useState<string[]>([]);

  // Objective → axis budget split. Seeded from a saved mix, else the objective
  // preset, else an even split. The objective chip pre-fills the sliders.
  const [objective, setObjective] = useState<BrandObjective | null>(null);
  const [objectiveMix, setObjectiveMix] = useState<ObjectiveMix>(
    brand?.strategy?.objectiveMix || OBJECTIVE_PRESETS.leads,
  );

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      await api.post(`/brands/${brandId}/geo-strategy/generate`, {});
      await refreshBrands();
    } catch (err: any) {
      setError(err.message || t('onboarding.geo.generateError'));
    } finally {
      setGenerating(false);
    }
  }, [brandId, refreshBrands]);

  // Auto-generate once on entry if nothing exists yet.
  useEffect(() => {
    if (!hasGeo && !generating) {
      handleGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When GEO lands, seed the source selection (pre-check first 5), build the
  // competitor list (resolve domains in the background), and fetch example
  // queries for the authority topics.
  useEffect(() => {
    if (!hasGeo || !geo) return;
    const sources = geo.targetAuthoritySources || [];
    setSelectedSources(sources.slice(0, 5));
    // Resolve each source to a domain so we can show its favicon. The generator
    // emits names ("Le Monde") not domains, so a bare-domain heuristic alone
    // shows no logo — resolve via the same Serper-backed resolver as competitors.
    sources.forEach((name) => {
      const direct = domainIfLooksLikeOne(name);
      if (direct) {
        setSourceDomains((prev) => ({ ...prev, [name]: direct }));
        return;
      }
      competitorsApi
        .resolveDomain(brandId, name)
        .then((res) => {
          if (res.domain) setSourceDomains((prev) => ({ ...prev, [name]: res.domain! }));
        })
        .catch(() => void 0);
    });

    // Exclude the brand itself from the competitor list — the GEO generator
    // sometimes echoes the brand's own name/domain into competitiveFocus.
    const ownDomain = domainIfLooksLikeOne(brand?.url || '');
    const ownName = (brand?.name || '').trim().toLowerCase();
    const isSelf = (name: string, domain: string | null) => {
      const n = name.trim().toLowerCase();
      if (ownName && n === ownName) return true;
      if (ownDomain && domain && domain === ownDomain) return true;
      if (ownDomain && domainIfLooksLikeOne(name) === ownDomain) return true;
      return false;
    };

    // Propose at most COMPETITOR_PROPOSE_MAX suggestions, and pre-select only up
    // to the tier cap (track: true for the first N) so the default selection
    // never exceeds the plan. Beyond the cap, rows are shown unselected — the
    // user can swap which ones are tracked, but the toggle blocks going over.
    const preselectMax =
      competitorLimit === -1
        ? COMPETITOR_PROPOSE_MAX
        : Math.min(competitorLimit, COMPETITOR_PROPOSE_MAX);
    const focus = (geo.competitiveFocus || [])
      .filter((name) => !isSelf(name, domainIfLooksLikeOne(name)))
      .slice(0, COMPETITOR_PROPOSE_MAX);
    setCompetitors(
      focus.map((name, i) => ({
        name,
        domain: domainIfLooksLikeOne(name),
        track: i < preselectMax,
      })),
    );
    // Resolve missing domains via the competitor resolver (best-effort). If a
    // resolved domain turns out to be the brand's own, drop that entry.
    focus.forEach((name) => {
      if (domainIfLooksLikeOne(name)) return;
      competitorsApi
        .resolveDomain(brandId, name)
        .then((res) => {
          if (!res.domain) return;
          if (ownDomain && res.domain === ownDomain) {
            // Resolved to the brand's own domain — remove by name (index-safe).
            setCompetitors((prev) => prev.filter((c) => c.name !== name));
            return;
          }
          setCompetitors((prev) =>
            prev.map((c) => (c.name === name ? { ...c, domain: res.domain } : c)),
          );
        })
        .catch(() => void 0);
    });

    // Example queries from the authority topics (brand keywords).
    const topics = (brand?.strategy?.keywords || []).slice(0, 6);
    if (topics.length > 0) {
      setExamplesLoading(true);
      brandQueriesApi
        .examples(brandId, topics)
        .then((res) => setExamples(res.examples || []))
        .catch(() => void 0)
        .finally(() => setExamplesLoading(false));
    }

    // Key phrases to highlight inside the strategic angle (verbatim substrings).
    if (geo.strategicAngle) {
      api
        .post<{ phrases: string[] }>('/brands/extract-key-phrases', { text: geo.strategicAngle })
        .then((res) => setAnglePhrases(res.phrases || []))
        .catch(() => void 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGeo]);

  const toggleSource = (s: string) =>
    setSelectedSources((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const trackedCount = competitors.filter((c) => c.track).length;
  const competitorsUnlimited = competitorLimit === -1;
  const competitorsAtCap = !competitorsUnlimited && trackedCount >= competitorLimit;

  const toggleCompetitorTrack = (idx: number) =>
    setCompetitors((prev) =>
      prev.map((c, i) => {
        if (i !== idx) return c;
        // Block turning tracking ON past the tier cap; turning OFF is always allowed.
        if (!c.track && competitorsAtCap) return c;
        return { ...c, track: !c.track };
      }),
    );

  const removeCompetitor = (idx: number) =>
    setCompetitors((prev) => prev.filter((_, i) => i !== idx));

  // Manual add is DOMAIN-FIRST: the user enters the competitor's real domain
  // (a name-only entry too often resolves to the wrong site, which poisons the
  // favicon + the shared DomainProfile). The display name is derived from the
  // host. The brand's own domain is rejected.
  const addCompetitor = () => {
    const domain = normalizeCompetitorDomain(competitorInput);
    if (!isValidCompetitorDomain(domain)) return;
    const ownDomain = domainIfLooksLikeOne(brand?.url || '');
    if (ownDomain && domain === ownDomain) {
      setCompetitorInput('');
      return;
    }
    if (competitors.some((c) => c.domain === domain)) {
      setCompetitorInput('');
      return;
    }
    const name = competitorNameFromDomain(domain);
    // Default new entries to tracked, UNLESS that would exceed the tier cap —
    // then add untracked (listed, not billed) so we never trigger a 429 at apply.
    setCompetitors((prev) => [...prev, { name, domain, track: !competitorsAtCap }]);
    setCompetitorInput('');
  };

  const selectObjective = (obj: BrandObjective) => {
    setObjective(obj);
    setObjectiveMix(OBJECTIVE_PRESETS[obj]);
  };

  const handleValidate = async () => {
    setValidating(true);
    setError(null);
    try {
      // Persist the objective mix + (agency) prompt budget onto the brand
      // strategy. These are generation-only knobs — the backend excludes them
      // from the validation hash — but PATCH /strategy still flips strategyMeta
      // to draft/stale on any save, which would make the GEO-validate gate
      // ("validez d'abord la Brand Strategy") refuse. So re-validate the Brand
      // Strategy immediately after, mirroring the PromptsStep pattern.
      await api.patch(`/brands/${brandId}/strategy`, {
        objectiveMix,
      });
      await api.post(`/brands/${brandId}/strategy/validate`, {});

      // Persist the trimmed authority-source selection if the user changed it.
      const original = geo?.targetAuthoritySources || [];
      const changed =
        selectedSources.length !== original.length ||
        selectedSources.some((s) => !original.includes(s));
      if (changed) {
        await api.patch(`/brands/${brandId}/geo-strategy`, {
          targetAuthoritySources: selectedSources,
        });
      }
      await api.post(`/brands/${brandId}/geo-strategy/validate`, {});

      // Add the checked competitors to Competitor Analysis (best-effort). A
      // real domain is required server-side, so skip any whose domain never
      // resolved — tracking them would 400 and the name-only entry would be
      // meaningless anyway.
      await Promise.allSettled(
        competitors
          .filter((c) => c.track && c.domain && isValidCompetitorDomain(c.domain))
          .map((c) =>
            competitorsApi.add(brandId, { name: c.name, domain: c.domain! }),
          ),
      );

      await onDone();
    } catch (err: any) {
      setError(err.message || t('onboarding.geo.validateError'));
      setValidating(false);
    }
  };

  return (
    <Card>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {t('onboarding.geo.title')}
          </Typography>
          {!generating && (
            <Tooltip
              title={
                (brand?.geoStrategyMeta?.generateCount ?? 0) >= GEO_GENERATE_MAX
                  ? t('onboarding.geo.regenerateLimit')
                  : ''
              }
              arrow
            >
              <span>
                <Button
                  size="small"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleGenerate}
                  disabled={validating || (brand?.geoStrategyMeta?.generateCount ?? 0) >= GEO_GENERATE_MAX}
                >
                  {t('onboarding.geo.regenerate')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {generating
            ? t('onboarding.geo.generatingSubtitle')
            : t('onboarding.geo.subtitle')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {generating || !hasGeo ? (
          <GeoSkeleton />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
            {/* Strategic angle — highlighted */}
            {geo!.strategicAngle && (
              <Box>
                <SectionLabel
                  label={t('onboarding.geo.strategicAngle.label')}
                  tooltip={t('onboarding.geo.strategicAngle.tooltip')}
                />
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'primary.50',
                    borderLeft: '4px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      lineHeight: 1.9,
                      '& mark': {
                        bgcolor: 'rgba(139, 92, 246, 0.22)',
                        color: 'inherit',
                        borderRadius: 0.5,
                        px: 0.25,
                      },
                    }}
                  >
                    <HighlightedPhrases text={geo!.strategicAngle} phrases={anglePhrases} />
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Target queries — example table */}
            <Box>
              <SectionLabel
                label={t('onboarding.geo.exampleQueries.label')}
                tooltip={t('onboarding.geo.exampleQueries.tooltip')}
              />
              <QueryExamplesTable examples={examples} loading={examplesLoading} />
            </Box>

            {/* Authority sources — favicon checkboxes */}
            {(geo!.targetAuthoritySources || []).length > 0 && (
              <Box>
                <SectionLabel
                  label={t('onboarding.geo.authoritySources.label')}
                  tooltip={t('onboarding.geo.authoritySources.tooltip')}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                  {(geo!.targetAuthoritySources || []).map((s, i) => (
                    <SourceCheckRow
                      key={`${s}-${i}`}
                      source={s}
                      domain={sourceDomains[s] || null}
                      selected={selectedSources.includes(s)}
                      onToggle={() => toggleSource(s)}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Competitive focus — resolved domains + add-to-analysis */}
            <Box>
              <SectionLabel
                label={
                  competitorsUnlimited
                    ? t('onboarding.geo.competitors.label')
                    : t('onboarding.geo.competitors.labelWithCount', {
                        tracked: trackedCount,
                        limit: competitorLimit,
                      })
                }
                tooltip={t('onboarding.geo.competitors.tooltip')}
              />
              {competitors.length > 0 && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mb: 1 }}>
                  {competitors.map((c, i) => (
                    <CompetitorRow
                      key={`${c.name}-${i}`}
                      name={c.name}
                      domain={c.domain}
                      track={c.track}
                      onToggle={() => toggleCompetitorTrack(i)}
                      onRemove={() => removeCompetitor(i)}
                    />
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  value={competitorInput}
                  onChange={(e) => setCompetitorInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCompetitor();
                    }
                  }}
                  fullWidth
                  size="small"
                  placeholder={t('onboarding.geo.competitors.addPlaceholder')}
                  error={
                    competitorInput.trim().length > 0 &&
                    !isValidCompetitorDomain(normalizeCompetitorDomain(competitorInput))
                  }
                  helperText={
                    competitorInput.trim().length > 0 &&
                    !isValidCompetitorDomain(normalizeCompetitorDomain(competitorInput))
                      ? t('onboarding.geo.competitors.invalidDomain')
                      : ' '
                  }
                />
                <Button
                  variant="outlined"
                  onClick={addCompetitor}
                  disabled={!isValidCompetitorDomain(normalizeCompetitorDomain(competitorInput))}
                  sx={{ minWidth: 44, px: 0, alignSelf: 'flex-start' }}
                >
                  <AddIcon fontSize="small" />
                </Button>
              </Box>
              {competitorsAtCap && (
                <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.5 }}>
                  {t('onboarding.geo.competitors.atCap', { limit: competitorLimit })}
                </Typography>
              )}
            </Box>

            {/* Objective + prompt volume + (minimized) axis split */}
            <Box>
              <SectionLabel
                label={t('onboarding.geo.objective.label')}
                tooltip={t('onboarding.geo.objective.tooltip')}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {OBJECTIVE_OPTIONS.map((o) => (
                  <Tooltip key={o.value} title={t(o.hintKey)} arrow>
                    <Chip
                      label={t(o.labelKey)}
                      onClick={() => selectObjective(o.value)}
                      color={objective === o.value ? 'primary' : 'default'}
                      variant={objective === o.value ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 600, px: 0.5 }}
                    />
                  </Tooltip>
                ))}
              </Box>

              {/* Axis split — minimized, descriptions only (no jargon labels) */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('onboarding.geo.axisSplit')}
              </Typography>
              <DistributionEditor
                segments={OBJECTIVE_SEGMENTS.map((s) => ({
                  key: s.key,
                  label: t(s.labelKey),
                  description: t(s.descriptionKey),
                  color: s.color,
                }))}
                value={objectiveMix as unknown as Record<keyof ObjectiveMix, number>}
                onChange={(next) => setObjectiveMix(next as ObjectiveMix)}
                compact
                hideLabel
              />
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onBack} disabled={generating || validating}>
              {t('onboarding.common.back')}
            </Button>
            {canQuit && <QuitOnboardingButton brandName={brand?.name} onQuit={onQuit} />}
          </Box>
          <Button
            variant="contained"
            onClick={handleValidate}
            disabled={!hasGeo || generating || validating}
          >
            {validating ? <CircularProgress size={20} color="inherit" /> : t('onboarding.common.validateContinue')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

// True-ish domain detector: "lemonde.fr" → "lemonde.fr"; "Le Monde" → null.
function domainIfLooksLikeOne(s: string): string | null {
  const t = (s || '').trim().toLowerCase();
  if (/^https?:\/\//.test(t)) {
    try {
      return new URL(t).hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  }
  // Bare host heuristic: contains a dot, no spaces, valid-ish TLD.
  if (!t.includes(' ') && /^[a-z0-9.-]+\.[a-z]{2,}$/.test(t)) {
    return t.replace(/^www\./, '');
  }
  return null;
}

// Normalize a user-typed competitor domain: lowercase, strip scheme/path/www.
function normalizeCompetitorDomain(input: string): string {
  let d = (input || '').trim().toLowerCase();
  if (!d) return '';
  d = d.replace(/^https?:\/\//, '').split('/')[0].split('?')[0].split('#')[0];
  return d.replace(/^www\./, '');
}

// Mirror of the backend `looksLikeDomain` + Competitors page `isValidDomain`.
function isValidCompetitorDomain(domain: string): boolean {
  const d = (domain || '').trim().toLowerCase();
  if (!d) return false;
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d) && /\.[a-z]{2,}$/.test(d);
}

// Display name from a domain: "lemonde.fr" → "Lemonde". Best-effort label only.
function competitorNameFromDomain(domain: string): string {
  const label = (domain.split('.')[0] || domain).replace(/[-_]/g, ' ').trim();
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : domain;
}

// Section header with optional info tooltip.
function SectionLabel({ label, tooltip }: { label: string; tooltip?: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} arrow>
          <InfoOutlinedIcon sx={{ fontSize: 15, color: 'action.active', cursor: 'help' }} />
        </Tooltip>
      )}
    </Box>
  );
}

// Render `text` with only the given phrases wrapped in <mark>. Phrases are
// verbatim substrings (LLM-extracted); longest-first so overlapping matches
// pick the larger group. Falls back to plain text when no phrases match.
function HighlightedPhrases({ text, phrases }: { text: string; phrases: string[] }) {
  const valid = phrases
    .filter((p) => p && text.includes(p))
    .sort((a, b) => b.length - a.length);
  if (valid.length === 0) return <>{text}</>;

  // Walk the string, carving out the earliest next match each step.
  const out: React.ReactNode[] = [];
  let rest = text;
  let key = 0;
  while (rest.length > 0) {
    let bestIdx = -1;
    let bestPhrase = '';
    for (const p of valid) {
      const idx = rest.indexOf(p);
      if (idx !== -1 && (bestIdx === -1 || idx < bestIdx || (idx === bestIdx && p.length > bestPhrase.length))) {
        bestIdx = idx;
        bestPhrase = p;
      }
    }
    if (bestIdx === -1) {
      out.push(<span key={key++}>{rest}</span>);
      break;
    }
    if (bestIdx > 0) out.push(<span key={key++}>{rest.slice(0, bestIdx)}</span>);
    out.push(
      <Box component="mark" key={key++}>
        {bestPhrase}
      </Box>,
    );
    rest = rest.slice(bestIdx + bestPhrase.length);
  }
  return <>{out}</>;
}

// Loading shimmer for the GEO generation phase.
function GeoSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Skeleton variant="text" width={140} height={20} />
        <Skeleton variant="rounded" height={72} sx={{ mt: 0.5 }} />
      </Box>
      <Box>
        <Skeleton variant="text" width={180} height={20} />
        <Skeleton variant="rounded" height={140} sx={{ mt: 0.5 }} />
      </Box>
      <Box>
        <Skeleton variant="text" width={160} height={20} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 0.5 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={40} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

const AXIS_COLORS = { narrative: '#8B5CF6', authority: '#00DDB3', referral: '#FF6B35' };

// Table header cell for an axis: coloured label + inline info tooltip.
function AxisHeaderCell({ label, color, tooltip }: { label: string; color: string; tooltip: string }) {
  return (
    <TableCell sx={{ fontWeight: 700, color }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {label}
        <Tooltip title={tooltip} arrow>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'action.active', cursor: 'help' }} />
        </Tooltip>
      </Box>
    </TableCell>
  );
}

// Clean preview table: one row per authority topic, 3 example queries.
function QueryExamplesTable({ examples, loading }: { examples: QueryExampleRow[]; loading: boolean }) {
  const { t } = useTranslation();
  if (loading) {
    return <Skeleton variant="rounded" height={160} />;
  }
  if (examples.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('onboarding.geo.exampleQueries.empty')}
      </Typography>
    );
  }
  const cell = (text: string, color: string) => (
    <TableCell sx={{ verticalAlign: 'top', borderLeft: `3px solid ${color}`, py: 1 }}>
      <Typography variant="caption" sx={{ lineHeight: 1.35, display: 'block' }}>
        {text || '—'}
      </Typography>
    </TableCell>
  );
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell sx={{ fontWeight: 700 }}>{t('onboarding.geo.exampleQueries.themeColumn')}</TableCell>
            <AxisHeaderCell
              label={t('onboarding.geo.exampleQueries.narrativeColumn')}
              color={AXIS_COLORS.narrative}
              tooltip={t('onboarding.geo.exampleQueries.narrativeTooltip')}
            />
            <AxisHeaderCell
              label={t('onboarding.geo.exampleQueries.authorityColumn')}
              color={AXIS_COLORS.authority}
              tooltip={t('onboarding.geo.exampleQueries.authorityTooltip')}
            />
            <AxisHeaderCell
              label={t('onboarding.geo.exampleQueries.referralColumn')}
              color={AXIS_COLORS.referral}
              tooltip={t('onboarding.geo.exampleQueries.referralTooltip')}
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {examples.map((row, i) => (
            <TableRow key={`${row.topic}-${i}`}>
              <TableCell sx={{ verticalAlign: 'top', fontWeight: 600, py: 1 }}>
                <Typography variant="caption" fontWeight={700}>
                  {row.topic}
                </Typography>
              </TableCell>
              {cell(row.narrative, AXIS_COLORS.narrative)}
              {cell(row.authority, AXIS_COLORS.authority)}
              {cell(row.referral, AXIS_COLORS.referral)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// Authority-source row: favicon + name + checkbox.
function SourceCheckRow({
  source,
  domain,
  selected,
  onToggle,
}: {
  source: string;
  domain: string | null;
  selected: boolean;
  onToggle: () => void;
}) {
  const favicon = useDomainFavicon(domain || undefined, 64);
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.5,
        borderRadius: 1.5,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'primary.50' : 'transparent',
      }}
    >
      <Checkbox checked={selected} size="small" sx={{ p: 0.5 }} />
      <Avatar src={favicon || undefined} sx={{ width: 20, height: 20, fontSize: 11, bgcolor: 'action.hover' }}>
        {source.charAt(0).toUpperCase()}
      </Avatar>
      <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
        {source}
      </Typography>
    </Box>
  );
}

// Single funnel slider for the audience mix: slide from "conscient du problème"
// (left, red) to "chercheur évaluateur" (right, green). The selected profile's
// label + description appear below and update live. Maps to the 4-way
// `audienceMix` via the peaked helpers so the backend shape is unchanged.
function AudienceFunnelSlider({
  mix,
  onChange,
}: {
  mix: AudienceMix;
  onChange: (next: AudienceMix) => void;
}) {
  const { t } = useTranslation();
  const pos = audienceMixToPos(mix);
  const idx = audiencePosToIndex(pos);
  const activeKey = AUDIENCE_FUNNEL[idx];
  // Snap the thumb to each band's centre so the position is unambiguous.
  const marks = AUDIENCE_FUNNEL.map((_, i) => ({
    value: Math.round(((i + 0.5) / AUDIENCE_FUNNEL.length) * 100),
  }));
  return (
    <Box>
      <Box sx={{ px: 0.5 }}>
        <Slider
          value={pos}
          min={0}
          max={100}
          step={null}
          marks={marks}
          onChange={(_, v) => onChange(audienceIndexToMix(audiencePosToIndex(v as number)))}
          sx={{
            '& .MuiSlider-rail': {
              opacity: 1,
              background: 'linear-gradient(90deg, #E53935 0%, #FB8C00 38%, #FDD835 62%, #43A047 100%)',
              height: 6,
            },
            '& .MuiSlider-track': { border: 'none', background: 'transparent', height: 6 },
            '& .MuiSlider-mark': { backgroundColor: 'rgba(0,0,0,0.25)', height: 10, width: 2 },
            '& .MuiSlider-thumb': {
              height: 20,
              width: 20,
              backgroundColor: '#fff',
              border: '2px solid currentColor',
              color: 'text.primary',
            },
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {t(AUDIENCE_START_LABEL_KEY)}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {t(AUDIENCE_END_LABEL_KEY)}
        </Typography>
      </Box>
      <Box
        sx={{
          p: 2,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'primary.main',
          bgcolor: 'primary.50',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          {idx + 1}/{AUDIENCE_FUNNEL.length}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'primary.dark' }}>
            {t(AUDIENCE_LABEL_KEYS[activeKey])}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {t(AUDIENCE_DESCRIPTION_KEYS[activeKey])}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <FormatQuoteIcon sx={{ fontSize: 16, color: 'primary.main', transform: 'scaleX(-1)' }} />
            <Typography
              variant="caption"
              fontStyle="italic"
              sx={{ color: 'text.primary', fontWeight: 600 }}
            >
              {t(AUDIENCE_EXAMPLE_KEYS[activeKey])}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// Competitor row: favicon + name (+ domain) + track checkbox + remove.
function CompetitorRow({
  name,
  domain,
  track,
  onToggle,
  onRemove,
}: {
  name: string;
  domain: string | null;
  track: boolean;
  onToggle: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const favicon = useDomainFavicon(domain || undefined, 64);
  // A competitor can only be tracked once its real domain is resolved — the
  // backend requires a valid domain (a name-only guess poisons the favicon +
  // DomainProfile). Until then the row is shown but not selectable.
  const hasDomain = !!domain;
  return (
    <Box
      onClick={hasDomain ? onToggle : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.5,
        borderRadius: 1.5,
        cursor: hasDomain ? 'pointer' : 'default',
        opacity: hasDomain ? 1 : 0.6,
        border: '1px solid',
        borderColor: track && hasDomain ? 'primary.main' : 'divider',
        bgcolor: track && hasDomain ? 'primary.50' : 'transparent',
      }}
    >
      <Checkbox checked={track && hasDomain} disabled={!hasDomain} size="small" sx={{ p: 0.5 }} />
      <Avatar src={favicon || undefined} sx={{ width: 20, height: 20, fontSize: 11, bgcolor: 'action.hover' }}>
        {name.charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {name}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {domain || t('onboarding.geo.competitors.resolvingDomain')}
        </Typography>
      </Box>
      <Box
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'text.disabled',
          '&:hover': { color: 'text.secondary' },
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </Box>
    </Box>
  );
}

// ── Part 3: Prompts ───────────────────────────────────────────────────────────
// Auto-generate proposals for all 3 axes, let the user deselect, apply, then
// fire the 3 brand-perception audits.

interface AxisProposals {
  axis: 'narrative' | 'authority' | 'referral';
  geoStrategyHash: string;
  items: GeneratedQueryProposal[];
  selected: boolean[];
  // Set when the regenerate call itself failed (e.g. a timeout during the
  // Bedrock fan-out) — distinct from a legitimate empty result — so the
  // review UI can offer a retry instead of "no prompts for this axis".
  failed?: boolean;
}

const PROMPT_LANGUAGES: { code: string; label: string; flag: string }[] = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
];

// Geographic scale → icon. Inferred zones carry a `scale` tag; this maps it to
// the matching size icon (village → house cluster … monde → globe).
type ZoneScale = 'village' | 'ville' | 'region' | 'pays' | 'europe' | 'monde';
const SCALE_ICON: Record<ZoneScale, SvgIconComponent> = {
  village: HolidayVillageIcon,
  ville: LocationCityIcon,
  region: MapIcon,
  pays: FlagIcon,
  europe: PublicIcon,
  monde: LanguageIcon,
};

interface InferredZone {
  name: string;
  scale: ZoneScale;
  /** ISO2 from the zone-inference LLM — persisted onto the Location for flags. */
  countryCode?: string;
}

// "Global" is a pseudo-zone meaning "don't anchor the prompt to any place" —
// it maps to NO zone at generation time (the generator already treats an empty
// zone set as no-zone). It is NEVER persisted as a real location and is stripped
// before the regenerate call. Always offered + selected by default so every
// brand gets at least the geography-agnostic prompts.
const GLOBAL_ZONE = 'Global';
// Strip the Global sentinel → the real zones list sent to generation/locations.
function realZones(zones: string[]): string[] {
  return zones.filter((z) => z !== GLOBAL_ZONE);
}

function PromptsStep({
  brandId,
  brand,
  onBack,
  onFinish,
  canQuit,
  onQuit,
}: {
  brandId: string;
  brand: Brand | null;
  onBack: () => void;
  onFinish: () => Promise<void>;
  canQuit: boolean;
  onQuit: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { refreshBrands } = useBrand();
  const { tenant } = useAuth();
  // Shared `prompts` gauge (tenant-wide, all 3 axes). The brand is new here so
  // existing active prompts ≈ 0; total SELECTED is what will be created. Disable
  // selecting more past the cap instead of failing at apply.
  const promptsLimit = tenant
    ? resolveEntitlement('prompts', tenant.tier, {
        override: tenant.settings?.quotaOverrides?.prompts,
        packs: tenant.settings?.activePacks?.prompts,
      }).limit
    : -1;
  const promptsUnlimited = promptsLimit === -1;
  // Zones + languages gauges — same tier-cap treatment as prompts/competitors.
  // -1 ⇒ unlimited (enterprise; agency for languages). "Global" is a pseudo-zone
  // (no real location), so it does NOT count against the zones cap.
  const zonesLimit = tenant
    ? resolveEntitlement('zones', tenant.tier, {
        override: tenant.settings?.quotaOverrides?.zones,
        packs: tenant.settings?.activePacks?.zones,
      }).limit
    : -1;
  const languagesLimit = tenant
    ? resolveEntitlement('languages', tenant.tier, {
        override: tenant.settings?.quotaOverrides?.languages,
        packs: tenant.settings?.activePacks?.languages,
      }).limit
    : -1;
  // 'config' (zones + languages) → 'review' (generated prompts).
  const [phase, setPhase] = useState<'config' | 'review'>('config');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [proposals, setProposals] = useState<AxisProposals[]>([]);
  // Axis currently being individually retried after a failed generation
  // (e.g. a timeout) — drives the per-axis "Réessayer" button's spinner.
  const [retryingAxis, setRetryingAxis] = useState<AxisProposals['axis'] | null>(null);
  const [finishing, setFinishing] = useState(false);

  // Scroll to top on config → review transition.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [phase]);

  // Config phase state. Global is selected by default so every brand gets the
  // geography-agnostic prompts; existing real locations are added on top.
  const existingZones = (brand?.locations || []).map((l) => l.zone).filter(Boolean) as string[];
  const [zones, setZones] = useState<string[]>([GLOBAL_ZONE, ...existingZones]);
  const [languages, setLanguages] = useState<string[]>(() => {
    // Default to French (product is French-first), even if discovery inferred
    // another language from the site. `fr` leads the seed; any other inferred
    // languages follow. The user can still change it.
    const inferred = brand?.strategy?.targetLanguages?.length
      ? brand.strategy.targetLanguages
      : [];
    const seed = ['fr', ...inferred.filter((l) => l.toLowerCase() !== 'fr')];
    // Never seed more languages than the tier allows.
    return languagesLimit === -1 ? seed : seed.slice(0, languagesLimit);
  });
  const [preparing, setPreparing] = useState(false);

  // Per-brand AI-engine allowlist — agency only. Persisted on
  // `brand.auditConfig.enginesEnabled` via PATCH /brands/:id/audit-config at
  // handlePrepare time (same endpoint the prompt-management popup uses). Seeds
  // from the saved value, else the full roster.
  const isAgency = tenant?.tier === 'agency';
  const [enginesEnabled, setEnginesEnabled] = useState<string[]>(
    brand?.auditConfig?.enginesEnabled?.length
      ? brand.auditConfig.enginesEnabled
      : ALL_ENGINES.map((e) => e.id),
  );
  const toggleEngine = (id: string) =>
    setEnginesEnabled((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id],
    );

  // Agency-only: total prompts to generate (split across axes by the objective
  // mix). Moved here from the GEO step — it's a generation knob and belongs next
  // to the language/zone/model controls that feed the same generate call. Passed
  // as the `promptBudget` regenerate override (avoids a mutate-then-read race)
  // AND persisted on the strategy in handlePrepare.
  const [promptBudget, setPromptBudget] = useState<number>(
    brand?.strategy?.promptBudget ?? 30,
  );

  // Agency-only: Brand-Perception schedule. ONE cadence, applied to all 3 BP
  // axes (kept simple). Defaults to daily — the checkbox below lets the user
  // deactivate it (`null` = "set it later", no EventBridge schedule created,
  // runs stay manual). When set, handlePrepare writes it to cadenceOverride
  // for narrative/authority/referral.
  const [bpSchedule, setBpSchedule] = useState<Cadence | null>({ interval: 1, unit: 'days' });
  // Cadence dropdown is tucked behind "Réglages avancés" — collapsed by default
  // since daily is the sane default and most agencies won't touch it.
  const [bpScheduleAdvanced, setBpScheduleAdvanced] = useState(false);

  // Agency credit balance (null for non-agency / before first load).
  const { balance } = useCredits();

  // Inferred concrete zones (calibrated by envergure), fetched on entry. The
  // proposals carry real place names + a scale tag for the icon.
  const [inferredZones, setInferredZones] = useState<InferredZone[]>([]);
  const [zonesLoading, setZonesLoading] = useState(true);
  const [zoneInput, setZoneInput] = useState('');

  useEffect(() => {
    let cancelled = false;
    setZonesLoading(true);
    api
      .post<{ zones: InferredZone[] }>(`/brands/${brandId}/infer-zones`, {})
      .then((res) => {
        if (cancelled) return;
        const zs = (res.zones || []).filter((z) => z?.name && SCALE_ICON[z.scale]);
        setInferredZones(zs);
        // Pre-check the first few (most relevant) on top of Global, bounded by
        // the tier zones cap, unless the brand already has locations (then
        // respect the existing selection).
        if (existingZones.length === 0 && zs.length > 0) {
          const n = zonesLimit === -1 ? 3 : Math.min(3, zonesLimit);
          setZones([GLOBAL_ZONE, ...zs.slice(0, n).map((z) => z.name)]);
        }
      })
      .catch(() => void 0)
      .finally(() => {
        if (!cancelled) setZonesLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  // Real (non-Global) zones count against the cap; Global is geography-agnostic.
  const realZoneCount = realZones(zones).length;
  const zonesAtCap = zonesLimit !== -1 && realZoneCount >= zonesLimit;

  const toggleZone = (label: string) =>
    setZones((prev) => {
      if (prev.includes(label)) return prev.filter((z) => z !== label);
      // Block adding a real zone past the cap (Global is exempt).
      if (label !== GLOBAL_ZONE && zonesAtCap) return prev;
      return [...prev, label];
    });

  const addZone = () => {
    const v = zoneInput.trim();
    if (!v || zones.includes(v) || zonesAtCap) return;
    setZones([...zones, v]);
    setZoneInput('');
  };

  // Manual zones the user typed that aren't in the inferred proposals (Global is
  // rendered separately as a pinned option, so exclude it here).
  const customZones = zones.filter(
    (z) => z !== GLOBAL_ZONE && !inferredZones.some((iz) => iz.name === z),
  );

  const languagesAtCap = languagesLimit !== -1 && languages.length >= languagesLimit;

  const toggleLanguage = (code: string) => {
    setLanguages((prev) => {
      // Unselect freely (incl. the last one) so a quota=1 user can SWAP language
      // — deselect the current, pick another. The "Continuer" button is gated on
      // languages.length === 0, so an empty selection can't proceed.
      if (prev.includes(code)) return prev.filter((c) => c !== code);
      if (languagesAtCap) return prev; // block past the tier cap
      return [...prev, code];
    });
  };

  const competitorCount = (brand?.competitors || []).length;

  // Selected engines per axis (intersect the allowlist with the axis roster).
  const selectedEngineCount = (axis: 'narrative' | 'authority' | 'referral') =>
    AXIS_ENGINE_ROSTER[axis].filter((e) => enginesEnabled.includes(e)).length;

  // ── Credit-aware bounding (agency only) ──
  // The onboarding finish fires 5 audits (website + earned_media + 3 BP axes).
  // For an agency each hits a server credit reserve; an over-commit would make
  // a first-run audit silently fail the gate. So we (a) auto-shrink the
  // website/EM caps to fit the balance, and (b) hard-cap BP prompts/models to
  // the remainder. Non-agency tiers aren't credit-metered → no bounding.
  const creditPlan = useMemo(() => {
    const UNBOUNDED = {
      bounded: false,
      maxPages: AGENCY_WEBSITE_MAX_PAGES,
      maxFetch: AGENCY_EM_MAX_FETCH,
      websiteCr: AGENCY_WEBSITE_MAX_PAGES * WEBSITE_AUDIT_AGENTS,
      emCr: AGENCY_EM_MAX_FETCH,
      bpBudgetCr: Infinity,
      bpFixedCr: 0,
      bpEngineBudgetCr: Infinity,
      available: Infinity,
    };
    // Only agency is credit-metered. Before the balance loads, stay unbounded
    // (the server gate is the real protection; this is a UX guard).
    if (!isAgency || !balance) return UNBOUNDED;

    const available = balance.available;
    // First-ever audit: a fixed, modest website + EM budget (30 pages → 90 cr,
    // 90 fetch → 90 cr) so the bulk of the trial balance stays free for prompts.
    // Clamp down if the balance can't even cover that (keeps a floor of 1).
    const maxPages = Math.max(
      1,
      Math.min(ONBOARDING_WEBSITE_PAGES, Math.floor(available / (3 * WEBSITE_AUDIT_AGENTS))),
    );
    const maxFetch = Math.max(1, Math.min(ONBOARDING_EM_FETCH, Math.floor(available / 3)));
    const websiteCr = maxPages * WEBSITE_AUDIT_AGENTS * CREDIT_COST.page_analyzed;
    const emCr = maxFetch * CREDIT_COST.fetch;

    const bpBudgetCr = Math.max(0, available - websiteCr - emCr);
    // Competitor matrix is probed on every BP run (all 3 axes), independent of
    // prompt count: competitors × 2 cr × 3 axes.
    const bpFixedCr = competitorCount * CREDIT_COST.competitor * 3;
    const bpEngineBudgetCr = Math.max(0, bpBudgetCr - bpFixedCr);

    return {
      bounded: true,
      maxPages,
      maxFetch,
      websiteCr,
      emCr,
      bpBudgetCr,
      bpFixedCr,
      bpEngineBudgetCr,
      available,
    };
  }, [isAgency, balance, competitorCount]);

  // Conservative prompt count the BP engine budget can afford (uses the largest
  // selected engine count so generation never overshoots what's selectable).
  const maxSelectedEngines = Math.max(
    1,
    ...(['narrative', 'authority', 'referral'] as const).map(selectedEngineCount),
  );
  const affordablePrompts = creditPlan.bounded
    ? Math.floor(creditPlan.bpEngineBudgetCr / maxSelectedEngines)
    : Number.MAX_SAFE_INTEGER;

  // Slider ceiling: the tier max (100), hard-capped by what the balance affords.
  const budgetSliderMax = creditPlan.bounded
    ? Math.max(10, Math.min(100, affordablePrompts))
    : 100;
  // Effective budget that will actually be generated (the slider can lag the cap
  // right after an engine toggle shrinks affordablePrompts).
  const effectivePromptBudget = creditPlan.bounded
    ? Math.max(0, Math.min(promptBudget, affordablePrompts))
    : promptBudget;

  // Indicator: total AI requests per cycle = Σ (per-axis prompts × per-axis
  // engines). The objective mix splits the budget across axes; we approximate
  // the per-axis prompt share with the mix so the number tracks reality.
  const objectiveMix = brand?.strategy?.objectiveMix;
  const estimatedRequests = (['narrative', 'authority', 'referral'] as const).reduce(
    (sum, axis) => {
      const weight = objectiveMix ? (objectiveMix[axis] ?? 0) / 100 : 1 / 3;
      const axisPrompts = Math.round(effectivePromptBudget * weight);
      return sum + axisPrompts * selectedEngineCount(axis);
    },
    0,
  );

  // Recurring Brand-Perception cost per cycle: Σ over the 3 BP axes of
  // (engine calls + competitor matrix). This is what one BP run costs — shown as
  // the headline "cost per run" under the model selector. Excludes website + EM
  // (those run on their own cadence, not part of a BP cycle).
  const bpCycleCost = (['narrative', 'authority', 'referral'] as const).reduce(
    (sum, axis) => {
      const weight = objectiveMix ? (objectiveMix[axis] ?? 0) / 100 : 1 / 3;
      const axisPrompts = Math.round(effectivePromptBudget * weight);
      return (
        sum +
        estimateAuditCost(AXIS_TO_AUDIT_TYPE[axis], {
          promptCount: axisPrompts,
          engineCount: selectedEngineCount(axis),
          competitorCount,
        }).total
      );
    },
    0,
  );

  // One-time credits reserved when onboarding fires its 5 first-run audits:
  // website (maxPages×3) + EM (maxFetch) + the first BP cycle. The website/EM
  // halves are a MAX (trued-up down at commit). Shown near the launch button.
  const estimatedFinishReserve = creditPlan.websiteCr + creditPlan.emCr + bpCycleCost;

  // Fetch proposals for a single axis (shared by the initial 3-axis fan-out
  // and the per-axis "Réessayer" button after a failed/timed-out call).
  const fetchAxisProposals = async (axis: AxisProposals['axis']): Promise<AxisProposals> => {
    // Global → no zone: the generator treats an empty zone list as geography-
    // agnostic. Non-Global zones still drive their locked per-zone prompts.
    const genZones = realZones(zones);
    // Agency: cap the generation budget to what the credit balance affords
    // (after the website+EM reserve), so the proposal table never offers more
    // prompts than the brand could actually launch.
    const effectiveBudget = creditPlan.bounded
      ? Math.max(0, Math.min(promptBudget, affordablePrompts))
      : promptBudget;
    return brandQueriesApi
      .regenerate(brandId, axis, {
        languages,
        zones: genZones,
        ...(isAgency ? { promptBudget: effectiveBudget } : {}),
      })
      .then((res) => ({
        axis,
        geoStrategyHash: res.geoStrategyHash,
        items: res.proposals,
        selected: res.proposals.map(() => true),
      }))
      .catch(() => ({
        axis,
        geoStrategyHash: '',
        items: [] as GeneratedQueryProposal[],
        selected: [] as boolean[],
        failed: true,
      }));
  };

  // Run query generation for all 3 axes in parallel.
  const generateProposals = async () => {
    setLoading(true);
    setError(null);
    const results = await Promise.all(AXES.map(({ axis }) => fetchAxisProposals(axis)));
    setProposals(results);
    setLoading(false);
  };

  // Re-run generation for ONE axis only (e.g. after a timeout), leaving the
  // other two axes' proposals + selections untouched.
  const retryAxis = async (axis: AxisProposals['axis']) => {
    setRetryingAxis(axis);
    const result = await fetchAxisProposals(axis);
    setProposals((prev) => prev.map((p) => (p.axis === axis ? result : p)));
    setRetryingAxis(null);
  };

  // Config → review: persist zones (locations) + languages (strategy), then
  // RE-VALIDATE both strategy layers (changing targetLanguages flips them to
  // stale, which would block the audit gate), then generate prompts.
  const handlePrepare = async () => {
    setPreparing(true);
    setError(null);
    try {
      // 1. Create any newly-added REAL zones as locations (never "Global").
      //    Carry the inferred country + scale so the dashboard renders a flag
      //    without re-classifying at read time (manual zones have no inference).
      const toCreate = realZones(zones).filter((z) => !existingZones.includes(z));
      for (const z of toCreate) {
        try {
          const inf = inferredZones.find((iz) => iz.name === z);
          await api.post(`/brands/${brandId}/locations`, {
            label: z,
            zone: z,
            ...(inf?.countryCode && { countryCode: inf.countryCode }),
            ...(inf?.scale && { scale: inf.scale }),
          });
        } catch {
          // Non-fatal — a zone that fails just won't drive geo-specific prompts.
        }
      }

      // 2. Update target languages + (agency) prompt budget if changed, then
      //    re-validate the strategy + GEO layers (the save flips them to
      //    draft/stale). promptBudget is a generation-only knob (excluded from
      //    the validation hash) but PATCH /strategy still flips the meta, so we
      //    re-validate. We also pass promptBudget as a regenerate override so
      //    generation doesn't depend on this write being read-consistent.
      const prev = brand?.strategy?.targetLanguages || [];
      const langChanged =
        languages.length !== prev.length || languages.some((l) => !prev.includes(l));
      const budgetChanged = isAgency && (brand?.strategy?.promptBudget ?? 30) !== promptBudget;
      if (langChanged || budgetChanged) {
        await api.patch(`/brands/${brandId}/strategy`, {
          ...(langChanged ? { targetLanguages: languages } : {}),
          ...(isAgency ? { promptBudget } : {}),
        });
        await api.post(`/brands/${brandId}/strategy/validate`, {});
        await api.post(`/brands/${brandId}/geo-strategy/validate`, {});
      }

      // 3. Persist the agency audit-config in ONE merge: engine allowlist, the
      //    modest first-audit website/EM cost caps (30 pages / 90 fetch — kept
      //    small so the trial balance stays free for prompts), and the optional
      //    BP schedule (same cadence for all 3 axes; null = "set it later" → no
      //    cadenceOverride written). Server ignores these for non-agency tiers.
      if (isAgency) {
        await api.patch(`/brands/${brandId}/audit-config`, {
          enginesEnabled,
          // Only persist a cap when it actually SHRINKS below the tier default —
          // a well-funded agency computes to the full 100/100 and should stay on
          // the tier default (no per-brand pin).
          ...(creditPlan.bounded && creditPlan.maxPages < AGENCY_WEBSITE_MAX_PAGES
            ? { maxPagesPerWebsiteAudit: creditPlan.maxPages }
            : {}),
          ...(creditPlan.bounded && creditPlan.maxFetch < AGENCY_EM_MAX_FETCH
            ? { maxFetchPerEarnedMedia: creditPlan.maxFetch }
            : {}),
          ...(bpSchedule
            ? {
                cadenceOverride: {
                  brand_narrative: bpSchedule,
                  brand_authority: bpSchedule,
                  brand_referral: bpSchedule,
                },
              }
            : {}),
        });
      }

      await refreshBrands();
      setPhase('review');
      await generateProposals();
    } catch (err: any) {
      setError(err.message || t('onboarding.prompts.prepareError'));
    } finally {
      setPreparing(false);
    }
  };

  const toggle = (axisIdx: number, itemIdx: number) => {
    setProposals((prev) => {
      const currentlyOn = prev[axisIdx]?.selected[itemIdx];
      // Block turning a prompt ON when the effective cap is reached (the lower of
      // the prompts entitlement and, for agency, what the credit balance affords
      // after the website+EM reserve). Turning OFF is always allowed.
      if (!currentlyOn && effectivePromptCap !== Number.MAX_SAFE_INTEGER) {
        const total = prev.reduce((sum, p) => sum + p.selected.filter(Boolean).length, 0);
        if (total >= effectivePromptCap) return prev;
      }
      return prev.map((p, i) =>
        i === axisIdx
          ? { ...p, selected: p.selected.map((s, j) => (j === itemIdx ? !s : s)) }
          : p,
      );
    });
  };

  // The cap that actually governs selection: prompts entitlement ∧ (agency)
  // credit-affordable prompt count. -1/unlimited entitlement → only the credit
  // cap bites; non-agency → only the entitlement.
  const effectivePromptCap = Math.min(
    promptsUnlimited ? Number.MAX_SAFE_INTEGER : promptsLimit,
    affordablePrompts,
  );
  const promptCapUnlimited = effectivePromptCap === Number.MAX_SAFE_INTEGER;

  const totalSelected = proposals.reduce(
    (sum, p) => sum + p.selected.filter(Boolean).length,
    0,
  );
  const promptsAtCap = !promptCapUnlimited && totalSelected >= effectivePromptCap;

  const handleFinish = async () => {
    setFinishing(true);
    setError(null);
    try {
      // 1. Apply selected proposals per axis. applyRegen never throws on a
      //    per-row cap hit — it returns those in `errors[]`. Collect them so a
      //    silent partial-apply surfaces to the user instead of vanishing.
      let applyErrors = 0;
      for (const p of proposals) {
        const items = p.items.filter((_, i) => p.selected[i]);
        if (items.length === 0) continue;
        const res = await brandQueriesApi.applyRegen(brandId, {
          axis: p.axis,
          geoStrategyHash: p.geoStrategyHash || undefined,
          items: items.map((it) => ({
            text: it.text,
            level: it.level,
            zone: it.zone,
            language: it.language,
            topic: it.topic,
          })),
        });
        applyErrors += res.errors?.length || 0;
      }
      if (applyErrors > 0) {
        // Non-fatal: some rows hit the per-tier cap. Tell the user, but still
        // fire audits on what WAS created so onboarding completes.
        setError(t('onboarding.prompts.partialApply', { n: applyErrors }));
      }

      // 2. Fire ALL first audits for the brand-new brand: website + earned media
      //    + the 3 BP axes. All tiers get the first run of each type (the
      //    run-now gate is per-type, so these don't race/block each other);
      //    cadence takes over afterwards. Best-effort — a per-type failure
      //    (e.g. an axis with zero queries applied) shouldn't block the others.
      const FIRST_RUN_TYPES = [
        'website',
        'earned_media',
        ...AXES.map(({ axis }) => AXIS_TO_AUDIT_TYPE[axis]),
      ];
      await Promise.allSettled(
        FIRST_RUN_TYPES.map((type) =>
          api.post('/audits', { type, config: {}, brandId }),
        ),
      );

      // Mark onboarding finished (write-once) so the app stops routing this
      // brand back into the wizard. Done AFTER prompts + first audits so a
      // partial finish never flips the marker. Non-fatal — onboarding still
      // completes for the user even if this single call fails (onFinish →
      // refreshBrands picks the marker up).
      await api.post(`/brands/${brandId}/complete-onboarding`, {}).catch(() => void 0);

      pushDataLayerEvent('onboarding_complete');
      await onFinish();
    } catch (err: any) {
      setError(err.message || t('onboarding.prompts.finishError'));
      setFinishing(false);
    }
  };

  // ── Config phase: zones + languages, then generate ──
  if (phase === 'config') {
    return (
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
            <Typography variant="h6" fontWeight={700}>
              {t('onboarding.prompts.config.title')}
            </Typography>
            {isAgency && balance && (
              <Tooltip
                arrow
                title={t('onboarding.prompts.config.creditsTooltip')}
              >
                <Chip
                  icon={<BoltIcon sx={{ fontSize: 16 }} />}
                  label={t('onboarding.prompts.config.creditsAvailable', { credits: formatNumber(balance.available) })}
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 700, flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('onboarding.prompts.config.subtitle')}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>
              {t('onboarding.prompts.config.zones')}
            </Typography>
            {zonesLimit !== -1 && (
              <Typography variant="caption" color={zonesAtCap ? 'error.main' : 'text.secondary'}>
                {t('onboarding.prompts.config.zonesCount', { n: realZoneCount, limit: zonesLimit })}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {t('onboarding.prompts.config.zonesHint')}
          </Typography>

          {zonesLoading ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mb: 2 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={40} />
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1,
                mb: 1.5,
              }}
            >
              {[
                { name: GLOBAL_ZONE, scale: 'monde' as ZoneScale },
                ...inferredZones,
                ...customZones.map((name) => ({ name, scale: 'ville' as ZoneScale })),
              ].map((z) => {
                const selected = zones.includes(z.name);
                // A real, unselected zone is blocked when at cap; Global is exempt.
                const blocked = !selected && z.name !== GLOBAL_ZONE && zonesAtCap;
                const ScopeIcon = SCALE_ICON[z.scale] || LocationCityIcon;
                return (
                  <Box
                    key={z.name}
                    onClick={() => !blocked && toggleZone(z.name)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1.5,
                      cursor: blocked ? 'not-allowed' : 'pointer',
                      opacity: blocked ? 0.5 : 1,
                      border: '1px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      bgcolor: selected ? 'primary.50' : 'transparent',
                    }}
                  >
                    <Checkbox checked={selected} disabled={blocked} size="small" sx={{ p: 0.5 }} />
                    <ScopeIcon sx={{ fontSize: 20, color: selected ? 'primary.main' : 'action.active' }} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
                      {z.name === GLOBAL_ZONE ? t('onboarding.prompts.config.globalZone') : z.name}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Manual add */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <TextField
              value={zoneInput}
              onChange={(e) => setZoneInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addZone();
                }
              }}
              fullWidth
              size="small"
              placeholder={t('onboarding.prompts.config.zonePlaceholder')}
            />
            <Button
              variant="outlined"
              onClick={addZone}
              disabled={!zoneInput.trim() || zonesAtCap}
              sx={{ minWidth: 44, px: 0 }}
            >
              <AddIcon fontSize="small" />
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {t('onboarding.prompts.config.languages')}
            </Typography>
            {languagesLimit !== -1 && (
              <Typography variant="caption" color={languagesAtCap ? 'error.main' : 'text.secondary'}>
                {t('onboarding.prompts.config.languagesCount', { n: languages.length, limit: languagesLimit })}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {PROMPT_LANGUAGES.map((l) => {
              const selected = languages.includes(l.code);
              const blocked = !selected && languagesAtCap;
              return (
                <Chip
                  key={l.code}
                  label={`${l.flag}  ${l.label}`}
                  onClick={() => !blocked && toggleLanguage(l.code)}
                  disabled={blocked}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                />
              );
            })}
          </Box>

          {/* Agency-only: which AI engines to probe (logo chips, name on hover). */}
          {isAgency && (
            <>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                {t('onboarding.prompts.config.aiModels')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {ALL_ENGINES.map((e) => {
                  const selected = enginesEnabled.includes(e.id);
                  return (
                    <Tooltip key={e.id} title={e.label} arrow>
                      <Box
                        onClick={() => toggleEngine(e.id)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 44,
                          height: 44,
                          borderRadius: 1.5,
                          cursor: 'pointer',
                          border: '1px solid',
                          borderColor: selected ? 'primary.main' : 'divider',
                          bgcolor: selected ? 'primary.50' : 'transparent',
                          opacity: selected ? 1 : 0.5,
                          transition: 'opacity .15s, border-color .15s',
                          '&:hover': { opacity: 1 },
                        }}
                      >
                        <EngineLogo engine={e.id} size={24} />
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
              {enginesEnabled.length === 0 && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
                  {t('onboarding.prompts.config.selectOneModel')}
                </Typography>
              )}

              {/* Prompt volume — moved here from the GEO step (it feeds the
                  generate call right below). Bounded by the credit balance. */}
              <Box sx={{ mb: 1, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {t('onboarding.prompts.config.promptVolume')}
                  </Typography>
                  <Typography variant="h6" fontWeight={800} color="primary.main">
                    {Math.min(promptBudget, budgetSliderMax)}
                  </Typography>
                </Box>
                <Slider
                  value={Math.min(promptBudget, budgetSliderMax)}
                  min={10}
                  max={budgetSliderMax}
                  step={5}
                  marks={[
                    { value: 10, label: '10' },
                    { value: budgetSliderMax, label: `${budgetSliderMax}` },
                  ]}
                  onChange={(_, v) => setPromptBudget(Math.min(v as number, budgetSliderMax))}
                  sx={{ '& .MuiSlider-thumb': { height: 22, width: 22 } }}
                />
                {creditPlan.bounded && promptBudget >= budgetSliderMax && budgetSliderMax < 100 && (
                  <Typography variant="caption" color="text.secondary">
                    {t('onboarding.prompts.config.cappedByCredits')}
                  </Typography>
                )}
              </Box>

              {/* Requests (volume) vs. cost (credits) — visually separated. */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 1.5, mb: 3 }}>
                {/* AI requests per run — volume, prominent. */}
                <Box
                  sx={{
                    flex: '1 1 200px',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('onboarding.prompts.config.requestsPerAudit')}
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                    ≈ {formatNumber(estimatedRequests)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('onboarding.prompts.config.requestsHint')}
                  </Typography>
                </Box>

                {/* Cost per run — credits, with explanation. */}
                <Box
                  sx={{
                    flex: '1 1 200px',
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('onboarding.prompts.config.bpCost')}
                    </Typography>
                    <Tooltip
                      arrow
                      title={t('onboarding.prompts.config.bpCostTooltip')}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 15, color: 'action.active', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="h5" fontWeight={800} color="primary.main" sx={{ lineHeight: 1.2 }}>
                    {t('onboarding.prompts.config.creditsValue', { credits: formatNumber(bpCycleCost) })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('onboarding.prompts.config.bpCostHint')}
                  </Typography>
                </Box>
              </Box>

              {/* BP schedule — one cadence for all 3 axes, defaults to daily.
                  Collapsed to a single info line + opt-out checkbox; the
                  cadence dropdown is tucked behind "Réglages avancés". */}
              <Box sx={{ mb: bpScheduleAdvanced ? 1 : 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {bpSchedule && (
                      <Typography variant="body2" color="text.secondary">
                        {t('onboarding.prompts.config.scheduleRunsInfo', {
                          cadence: describeCadence(bpSchedule).toLowerCase(),
                        })}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox
                        size="small"
                        checked={bpSchedule === null}
                        onChange={(e) =>
                          setBpSchedule(e.target.checked ? null : { interval: 1, unit: 'days' })
                        }
                        sx={{ p: 0 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {t('onboarding.prompts.config.scheduleDeactivate')}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setBpScheduleAdvanced((v) => !v)}
                  >
                    {t('onboarding.prompts.config.advancedSettings')}
                  </Button>
                </Box>
                {bpSchedule === null && (
                  <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.5 }}>
                    {t('onboarding.prompts.config.scheduleDeactivateWarning')}
                  </Typography>
                )}
              </Box>
              {bpScheduleAdvanced && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {t('onboarding.prompts.config.scheduleTitle')}
                  </Typography>
                  <Select
                    size="small"
                    disabled={bpSchedule === null}
                    value={
                      bpSchedule
                        ? describeCadence(bpSchedule)
                        : describeCadence({ interval: 1, unit: 'days' })
                    }
                    onChange={(e) => {
                      const opt = BP_SCHEDULE_OPTIONS.find(
                        (o) => describeCadence(o.value) === e.target.value,
                      );
                      if (opt) setBpSchedule(opt.value);
                    }}
                    sx={{ minWidth: 220 }}
                  >
                    {BP_SCHEDULE_OPTIONS.map((o) => (
                      <MenuItem key={o.labelKey} value={describeCadence(o.value)}>
                        {t(o.labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}
            </>
          )}

          {/* Total launch budget — the one-time credits held when onboarding
              fires its first audits (BP + a MAX for website & médias acquis). */}
          {isAgency && balance && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mt: 2,
                p: 1.5,
                border: '1px solid',
                borderColor: estimatedFinishReserve > balance.available ? 'warning.main' : 'divider',
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  {t('onboarding.prompts.config.launchBudget')}
                </Typography>
                <Tooltip
                  arrow
                  title={t('onboarding.prompts.config.launchBudgetTooltip')}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.active', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Typography
                variant="subtitle1"
                fontWeight={800}
                color={estimatedFinishReserve > balance.available ? 'warning.main' : 'text.primary'}
              >
                {t('onboarding.prompts.config.launchBudgetValue', {
                  reserve: formatNumber(estimatedFinishReserve),
                  available: formatNumber(balance.available),
                })}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={onBack} disabled={preparing}>
                {t('onboarding.common.back')}
              </Button>
              {canQuit && <QuitOnboardingButton brandName={brand?.name} onQuit={onQuit} />}
            </Box>
            <Button
              variant="contained"
              onClick={handlePrepare}
              disabled={
                preparing ||
                languages.length === 0 ||
                (isAgency && enginesEnabled.length === 0)
              }
            >
              {preparing ? <CircularProgress size={20} color="inherit" /> : t('onboarding.prompts.config.generate')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // While generating, show the typewriter explainer (L1→L5) instead of the list.
  if (loading) {
    return (
      <Box>
        <TypewriterIntro segments={promptsGeneratingSegments(t)} speedMs={20} />
        <Box sx={{ mt: 2 }}>
          <LinearProgress sx={{ borderRadius: 1 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Card>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight={600}>
            {t('onboarding.prompts.review.title')}
          </Typography>
          {/* Plan quota — non-agency only (agency is credit-metered, where this
              number is the credit-affordable cap, not a quota → confusing). */}
          {!isAgency && !promptsUnlimited && (
            <Chip
              size="small"
              color={promptsAtCap ? 'warning' : 'default'}
              variant={promptsAtCap ? 'filled' : 'outlined'}
              label={t('onboarding.prompts.review.selectedCount', { n: totalSelected, limit: promptsLimit })}
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('onboarding.prompts.review.description')}
          {promptsAtCap &&
            (creditPlan.bounded && effectivePromptCap < promptsLimit
              ? ` ${t('onboarding.prompts.review.atCapCredits')}`
              : ` ${t('onboarding.prompts.review.atCapPlan')}`)}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {(
          AXES.map(({ axis, labelKey, color }, axisIdx) => {
            const p = proposals[axisIdx];
            return (
              <Box key={axis} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t(labelKey)}
                  </Typography>
                  {axis === 'referral' && (
                    <Tooltip title={t('onboarding.prompts.review.levelLadderTooltip')} arrow>
                      <InfoOutlinedIcon sx={{ fontSize: 15, color: 'action.active', cursor: 'help' }} />
                    </Tooltip>
                  )}
                  <Chip
                    size="small"
                    label={`${p?.selected.filter(Boolean).length || 0} / ${p?.items.length || 0}`}
                    variant="outlined"
                  />
                </Box>
                {p?.failed ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="body2" color="error.main">
                      {t('onboarding.prompts.review.generationFailed')}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        retryingAxis === axis ? (
                          <CircularProgress size={14} />
                        ) : (
                          <RefreshIcon fontSize="small" />
                        )
                      }
                      disabled={retryingAxis === axis}
                      onClick={() => retryAxis(axis)}
                    >
                      {t('onboarding.prompts.review.retry')}
                    </Button>
                  </Box>
                ) : !p || p.items.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('onboarding.prompts.review.noneForAxis')}
                  </Typography>
                ) : (
                  (() => {
                    // Row renderer (shared by flat + grouped layouts). itemIdx is
                    // the index into p.items so toggle() stays correct.
                    const renderRow = (it: GeneratedQueryProposal, itemIdx: number) => (
                      <Box
                        key={it.textHash}
                        onClick={() => toggle(axisIdx, itemIdx)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1,
                          borderRadius: 1,
                          cursor: 'pointer',
                          border: '1px solid',
                          borderColor: p.selected[itemIdx] ? color : 'divider',
                          bgcolor: p.selected[itemIdx] ? `${color}11` : 'transparent',
                        }}
                      >
                        <CheckCircleIcon
                          sx={{ fontSize: 18, color: p.selected[itemIdx] ? color : 'action.disabled' }}
                        />
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {it.text}
                        </Typography>
                        {it.level && it.level !== 'pending' && (
                          <Chip size="small" label={it.level} variant="outlined" />
                        )}
                      </Box>
                    );

                    // Authority + referral group by topic; narrative stays flat.
                    const hasTopics =
                      axis !== 'narrative' && p.items.some((it) => it.topic);
                    if (!hasTopics) {
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {p.items.map((it, i) => renderRow(it, i))}
                        </Box>
                      );
                    }

                    // Preserve first-seen topic order; untagged → "Autres".
                    const order: string[] = [];
                    const byTopic = new Map<string, number[]>();
                    p.items.forEach((it, i) => {
                      const key = it.topic || t('onboarding.prompts.review.otherTopic');
                      if (!byTopic.has(key)) {
                        byTopic.set(key, []);
                        order.push(key);
                      }
                      byTopic.get(key)!.push(i);
                    });

                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {order.map((topic) => (
                          <Box key={topic}>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ color, display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.3 }}
                            >
                              {topic}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {byTopic.get(topic)!.map((i) => renderRow(p.items[i], i))}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    );
                  })()
                )}
              </Box>
            );
          })
        )}

        <Alert severity="info" sx={{ mt: 4 }}>
          {t('onboarding.prompts.review.launchInfoIntro', { n: totalSelected })}{' '}
          {isAgency ? (
            bpSchedule ? (
              <Trans
                i18nKey="onboarding.prompts.review.launchInfoAgencyScheduled"
                values={{ cadence: describeCadence(bpSchedule).toLowerCase() }}
                components={{ strong: <strong /> }}
              />
            ) : (
              <Trans
                i18nKey="onboarding.prompts.review.launchInfoAgencyManual"
                components={{ strong: <strong /> }}
              />
            )
          ) : (
            <Trans
              i18nKey="onboarding.prompts.review.launchInfoAuto"
              components={{ strong: <strong /> }}
            />
          )}{' '}
          {t('onboarding.prompts.review.launchInfoOutro')}
        </Alert>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onBack} disabled={finishing}>
              {t('onboarding.common.back')}
            </Button>
            {canQuit && <QuitOnboardingButton brandName={brand?.name} onQuit={onQuit} />}
          </Box>
          <Button
            variant="contained"
            startIcon={finishing ? undefined : <RocketLaunchIcon />}
            onClick={handleFinish}
            disabled={loading || finishing || totalSelected === 0}
          >
            {finishing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('onboarding.prompts.review.launchButton', { n: totalSelected })
            )}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
